import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { DirectusService } from '../core/services/directus.service';

interface TicketDraft {
  name: string;
  description: string;
  pricingMethod: 'fixed' | 'free';
  price: number;
  quantityMode: 'unlimited' | 'limited';
  quantityTotal: number | null;
  maxQtyPerOrder: number;
  status: 'available' | 'hidden' | 'sold_out' | 'archived';
  descriptionFooter: string;
  visibility: 'show' | 'hide';
}

interface CategoryOption {
  id: string;
  name: string;
}

interface MemberOption {
  id: string;
  name: string;
  phone: string;
}

interface FeatureOption {
  id: string;
  key: string;
  name: string;
  description: string;
  selected: boolean;
}

interface LocationSelection {
  venueName: string;
  address: string;
  mapQuery: string;
}

interface LocationSuggestion {
  placeId?: string;
  venueName: string;
  address: string;
  mapQuery: string;
}

interface TimeOption {
  value: string;
  label: string;
}

type OverviewModuleType = 'text' | 'image' | 'video';

interface OverviewModule {
  id: string;
  type: OverviewModuleType;
  textHtml: string;
  fileId: string;
  assetUrl: string;
  videoUrl: string;
}

interface EventSlide {
  id: string;
  type: 'image' | 'video';
  fileId: string;
  assetUrl: string;
  croppedPreviewUrl?: string;
  cropX?: number;
  cropY?: number;
  cropZoom?: number;
  cropBoxX?: number;
  cropBoxY?: number;
  cropBoxWidth?: number;
  cropBoxHeight?: number;
}

interface FaqItem {
  id: string;
  question: string;
  answer: string;
}

interface LineupSpeaker {
  id: string;
  fileId: string;
  assetUrl: string;
  croppedPreviewUrl?: string;
  cropBoxX: number;
  cropBoxY: number;
  cropBoxWidth: number;
  cropBoxHeight: number;
  name: string;
  tagline: string;
  description: string;
}

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <section class="wizard-shell">
      <header class="wizard-header">
        <div>
          <p class="eyebrow">NUSANTARA OPERATIONS</p>
          <h1>{{ isEditMode ? 'Edit event' : 'New event' }}</h1>
        </div>
        <div class="header-actions">
          <button type="button" class="btn" (click)="cancel()" [disabled]="submitting">Cancel</button>
          <button type="button" class="btn btn-primary" (click)="nextOrSubmit()" [disabled]="submitting || uploadingImage || uploadingSlideMedia || uploadingOverviewMedia || searchingLocation">
            {{ submitting ? 'Saving...' : (isEditMode ? 'Save changes' : 'Save event details') }}
          </button>
        </div>
      </header>

      <nav class="step-nav">
        <button
          type="button"
          *ngFor="let label of steps; let idx = index"
          [class.active]="step === idx + 1"
          (click)="goToStep(idx + 1)">
          <span>{{ idx + 1 }}</span>{{ label }}
        </button>
      </nav>

      <p class="error" *ngIf="error">{{ error }}</p>
      <p class="success" *ngIf="success">{{ success }}</p>

      <section *ngIf="step === 1" class="step-layout">
        <article class="panel media-hero-panel" *ngIf="!mediaEditorOpen">
          <button type="button" class="media-hero" (click)="openMediaEditor()" [disabled]="uploadingSlideMedia || submitting">
            <img
              *ngIf="heroMediaBackgroundUrl"
              [src]="heroMediaBackgroundUrl"
              alt=""
              class="media-hero-bg"
              [style.object-position]="imageObjectPosition(coverImageSlide)"
              [style.transform]="imageTransform(coverImageSlide)"
            />
            <div
              *ngIf="!heroMediaBackgroundUrl"
              class="media-hero-bg media-hero-bg-fallback"
            ></div>
            <span class="media-hero-plus" [class.done]="hasAttachedMedia" (click)="openMediaEditor($event)">
              {{ hasAttachedMedia ? '✓' : '+' }}
            </span>
            <div class="media-hero-cta" *ngIf="!hasAttachedMedia" (click)="openMediaEditor($event)">
              <span class="media-hero-cta-icon">⇪</span>
              <span>Upload photos</span>
              <span>and videos</span>
            </div>
          </button>
        </article>

        <article #mediaEditorPanel class="panel slides-editor-panel" *ngIf="mediaEditorOpen">
          <h2>Add images and video</h2>

          <section class="media-section">
            <h3>Images</h3>
            <p class="hint">Add photos to show what your event will be about. You can upload up to 10 images.</p>
            <input #slideImageInput type="file" accept="image/*" hidden (change)="onSlideImageSelected($event)" />
            <div class="image-library" *ngIf="imageSlides.length > 0; else imageDropZoneOnly">
              <div
                class="image-preview-card"
                [class.drag-over]="slideImageDragOver"
                (dragover)="onSlideImageDragOver($event)"
                (dragleave)="onSlideImageDragLeave($event)"
                (drop)="onSlideImageDrop($event)"
              >
                <img
                  *ngIf="previewImageSlide"
                  [src]="displayImageUrl(previewImageSlide)"
                  alt=""
                  [style.object-fit]="'cover'"
                  [style.object-position]="imageObjectPosition(previewImageSlide)"
                  [style.transform]="imageTransform(previewImageSlide)"
                />
                <div class="image-preview-overlay" *ngIf="previewImageSlide">
                  <button
                    type="button"
                    class="overlay-action"
                    aria-label="Crop image"
                    (click)="previewImageSlide && openImageCropper(previewImageSlide.id)"
                  >
                    <svg class="overlay-icon-crop" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M5 6h2" />
                      <path d="M17 18h2" />
                      <path d="M8 3v13a2 2 0 0 0 2 2h9" />
                      <path d="M10 7h7a2 2 0 0 1 2 2v12" />
                      <path d="M10 12v3h3" />
                      <path d="M16 9h-2v2" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    class="overlay-action danger"
                    aria-label="Remove image"
                    (click)="removeCurrentPreviewImage()"
                  >
                    <svg class="overlay-icon-trash" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M4 7h16" />
                      <path d="M9 7V5h6v2" />
                      <path d="M8 7l1 12h6l1-12" />
                      <path d="M10.5 10.5v6" />
                      <path d="M13.5 10.5v6" />
                    </svg>
                  </button>
                </div>
              </div>
              <div class="thumb-row">
                <button type="button" class="thumb-add" (click)="slideImageInput.click()" [disabled]="uploadingSlideMedia || submitting">+</button>
                <div class="thumb-entry" *ngFor="let slide of imageSlides; let i = index">
                  <button
                    type="button"
                    class="thumb-item"
                    [class.active]="slide.id === selectedImagePreviewId"
                    [class.dragging]="draggingThumbId === slide.id"
                    [class.drag-target]="dragOverThumbId === slide.id && draggingThumbId !== slide.id"
                    draggable="true"
                    (dragstart)="onThumbDragStart(slide.id, $event)"
                    (dragend)="onThumbDragEnd()"
                    (dragenter)="onThumbDragEnter(slide.id, $event)"
                    (dragover)="onThumbDragOverTarget(slide.id, $event)"
                    (drop)="onThumbDrop(slide.id, $event)"
                    (click)="selectImagePreview(slide.id)"
                  >
                    <img [src]="displayImageUrl(slide)" alt="" draggable="false" [style.object-position]="imageObjectPosition(slide)" [style.transform]="imageTransform(slide)" />
                  </button>
                  <span class="thumb-cover-badge" *ngIf="i === 0">Cover</span>
                </div>
              </div>
            </div>
            <ng-template #imageDropZoneOnly>
              <div
                class="dropzone"
                [class.drag-over]="slideImageDragOver"
                (dragover)="onSlideImageDragOver($event)"
                (dragleave)="onSlideImageDragLeave($event)"
                (drop)="onSlideImageDrop($event)"
              >
                <p class="drop-title">Drag and drop an image or</p>
                <button type="button" class="btn" (click)="slideImageInput.click()" [disabled]="uploadingSlideMedia || submitting">
                  Upload image
                </button>
              </div>
            </ng-template>
            <p class="hint" *ngIf="uploadingSlideMedia">Uploading slide media...</p>
            <ul class="media-info">
              <li>Recommended image size: 2160 x 1080px</li>
              <li>Alternative size: 960 x 350px</li>
              <li>Maximum file size: 10MB</li>
              <li>Supported image files: JPEG, PNG</li>
              <li>Minimum dimensions: 600 x 600px</li>
            </ul>
          </section>

          <section class="media-section">
            <h3>Video</h3>
            <p class="hint">Add a video to show your event's vibe. The video will appear with your event images.</p>
            <input #slideVideoInput type="file" accept="video/*" hidden (change)="onSlideVideoSelected($event)" />
            <div class="dropzone compact">
              <button type="button" class="btn" (click)="slideVideoInput.click()" [disabled]="uploadingSlideMedia || submitting">
                Upload video
              </button>
            </div>
            <ul class="media-info">
              <li>Min resolution: 480p</li>
              <li>Ratio: any vertical</li>
              <li>Length: up to 1 min</li>
              <li>Formats: MP4, MOV</li>
            </ul>
          </section>

          <div class="slides-grid" *ngIf="videoSlides.length > 0">
            <article class="slide-item" *ngFor="let slide of videoSlides; let i = index">
              <video [src]="slide.assetUrl" controls preload="metadata"></video>
              <div class="slide-meta">
                <span>Video {{ i + 1 }}</span>
                <button type="button" class="link danger" (click)="removeEventSlide(slide.id)">Remove</button>
              </div>
            </article>
          </div>
        </article>

        <article class="panel">
          <h2>General info</h2>
          <div class="field-grid">
            <label>
              Event name
              <input [(ngModel)]="details.title" placeholder="Give it a short, distinct name" />
            </label>
            <label>
              Project Director
              <select [(ngModel)]="details.projectDirectorId" (ngModelChange)="onProjectDirectorChange()">
                <option value="">— Select member —</option>
                <option *ngFor="let m of members" [value]="m.id">{{ m.name }}</option>
              </select>
            </label>
            <label>
              Project Director's Phone
              <input [(ngModel)]="details.projectDirectorPhone" placeholder="62812xxxxxxx" readonly />
            </label>
            <label>
              Event category
              <select [(ngModel)]="details.categoryId">
                <option value="">No category</option>
                <option *ngFor="let cat of categories" [value]="cat.id">{{ cat.name }}</option>
              </select>
            </label>
          </div>
        </article>

        <article
          #dateLocationPanel
          class="panel location-panel date-location-panel"
          [class.date-location-collapsed]="dateLocationCollapsed"
          (click)="expandDateLocation()">
          <div class="overview-head">
            <h2>Date and location</h2>
            <span class="overview-complete-badge" *ngIf="dateLocationCollapsed && isDateLocationComplete">✓</span>
          </div>

          <ng-container *ngIf="!dateLocationCollapsed; else dateLocationCollapsedView">
            <h3 class="subhead">Type of event</h3>
            <div class="chip-row two-up">
              <button type="button" class="choice" [class.active]="details.eventTimeMode === 'single'" (click)="setEventTimeMode('single')">Single event</button>
              <button type="button" class="choice" [class.active]="details.eventTimeMode === 'recurring'" (click)="setEventTimeMode('recurring')">Recurring event</button>
            </div>

            <h3 class="subhead">Date and time</h3>
            <div class="field-grid four-col">
              <label>
                Start date
                <input type="date" lang="en-US" class="date-input" [(ngModel)]="details.startDate" (ngModelChange)="onDateLocationFieldChange()" />
              </label>
              <label>
                Start time
                <select [(ngModel)]="details.startTime" (ngModelChange)="onDateLocationFieldChange()">
                  <option value="">Select time</option>
                  <option *ngFor="let option of timeOptions" [value]="option.value">{{ option.label }}</option>
                </select>
              </label>
              <label>
                End date
                <input type="date" lang="en-US" class="date-input" [(ngModel)]="details.endDate" (ngModelChange)="onDateLocationFieldChange()" />
              </label>
              <label>
                End time
                <select [(ngModel)]="details.endTime" (ngModelChange)="onDateLocationFieldChange()">
                  <option value="">Select time</option>
                  <option *ngFor="let option of timeOptions" [value]="option.value">{{ option.label }}</option>
                </select>
              </label>
            </div>

            <h3 class="subhead">Location</h3>
            <div class="chip-row two-up">
              <button type="button" class="choice" [class.active]="details.locationMode === 'physical'" (click)="setLocationMode('physical')">Physical</button>
              <button type="button" class="choice" [class.active]="details.locationMode === 'online'" (click)="setLocationMode('online')">Online</button>
            </div>

            <div *ngIf="details.locationMode === 'online'" class="online-info-box">
              Online events have unique event pages where you can add links to livestreams and more
            </div>

            <ng-container *ngIf="details.locationMode === 'physical'">
              <div class="field-grid" *ngIf="!selectedLocation">
                <label class="span-2">
                  Event location
                  <div #googlePlacesHost class="google-places-host"></div>
                  <p class="hint" *ngIf="searchingLocation">Loading Google Places...</p>
                  <p class="location-error" *ngIf="locationError">{{ locationError }}</p>
                </label>
              </div>

              <div class="field-grid" *ngIf="selectedLocation">
                <label>
                  Venue name
                  <input [(ngModel)]="selectedLocation.venueName" (ngModelChange)="onDateLocationFieldChange()" />
                </label>
                <label>
                  Address
                  <input [(ngModel)]="selectedLocation.address" (ngModelChange)="onDateLocationFieldChange()" (blur)="syncMapFromAddress()" />
                </label>
                <label class="span-2">
                  Google Map
                  <iframe
                    class="map-preview"
                    [src]="googleMapEmbedUrlSafe"
                    loading="lazy"
                    referrerpolicy="no-referrer-when-downgrade"></iframe>
                </label>
                <button type="button" class="link reset-location" (click)="resetLocation()">Reset location</button>
              </div>
            </ng-container>
          </ng-container>

          <ng-template #dateLocationCollapsedView>
            <section class="date-location-collapsed-body">
              <div class="date-location-collapsed-grid">
                <div class="date-location-collapsed-col">
                  <h3>Date and time</h3>
                  <p class="date-location-collapsed-line">🗓️ {{ dateLocationCollapsedDateTime }}</p>
                </div>
                <div class="date-location-collapsed-divider"></div>
                <div class="date-location-collapsed-col">
                  <h3>Location</h3>
                  <p class="date-location-collapsed-line">📍 {{ dateLocationCollapsedLocationTitle }}</p>
                  <p class="date-location-collapsed-address" *ngIf="dateLocationCollapsedLocationAddress">
                    {{ dateLocationCollapsedLocationAddress }}
                  </p>
                </div>
              </div>
              <iframe
                *ngIf="details.locationMode === 'physical' && selectedLocation"
                class="map-preview date-location-collapsed-map"
                [src]="googleMapEmbedUrlSafe"
                loading="lazy"
                referrerpolicy="no-referrer-when-downgrade"></iframe>
            </section>
          </ng-template>
        </article>

        <article #overviewPanel class="panel overview-panel" [class.overview-collapsed]="overviewCollapsed" (click)="expandOverview()">
          <div class="overview-head">
            <h2>Overview</h2>
            <span class="overview-complete-badge" *ngIf="overviewCollapsed && isOverviewComplete">✓</span>
          </div>

          <ng-container *ngIf="!overviewCollapsed; else overviewCollapsedView">
            <p class="hint">Add details about your event. You can mix rich text, images, and videos.</p>
            <p class="hint" [class.limit-breach]="isOverviewLimitExceeded">
              Each text block: max {{ OVERVIEW_TEXT_MAX_CHARS }} characters ·
              Images: {{ overviewImageCount }}/{{ OVERVIEW_MAX_IMAGES }} ·
              Videos: {{ overviewVideoCount }}/{{ OVERVIEW_MAX_VIDEOS }}
            </p>

            <div class="module-list">
              <article class="module-card" *ngFor="let module of overviewModules; let i = index; trackBy: trackByOverviewModuleId">
                <header class="module-head">
                  <strong>{{ moduleLabel(module.type) }} {{ i + 1 }}</strong>
                  <div class="inline-actions">
                    <button type="button" class="btn small" (click)="moveOverviewModuleUp(i)" [disabled]="i === 0">Up</button>
                    <button type="button" class="btn small" (click)="moveOverviewModuleDown(i)" [disabled]="i === overviewModules.length - 1">Down</button>
                    <button type="button" class="link danger" (click)="removeOverviewModule(module.id)">Delete</button>
                  </div>
                </header>

                <ng-container *ngIf="module.type === 'text'">
                  <div class="editor-toolbar">
                    <button type="button" (click)="applyModuleEditorCommand(module.id, 'bold')"><b>B</b></button>
                    <button type="button" (click)="applyModuleEditorCommand(module.id, 'italic')"><i>I</i></button>
                    <button type="button" (click)="applyModuleEditorCommand(module.id, 'insertUnorderedList')">• List</button>
                    <button type="button" (click)="applyModuleEditorCommand(module.id, 'insertOrderedList')">1. List</button>
                    <button type="button" (click)="insertModuleLink(module.id)">Link</button>
                  </div>
                  <div
                    class="rich-editor"
                    [attr.id]="'module-editor-' + module.id"
                    contenteditable="true"
                    [innerHTML]="module.textHtml"
                    (input)="onOverviewTextInput(module.id, $event)"
                    [attr.data-placeholder]="'Write event overview details...'">
                  </div>
                  <p class="hint" [class.limit-breach]="overviewTextModuleCharCount(module) > OVERVIEW_TEXT_MAX_CHARS">
                    Text block: {{ overviewTextModuleCharCount(module) }}/{{ OVERVIEW_TEXT_MAX_CHARS }} characters
                  </p>
                </ng-container>

                <ng-container *ngIf="module.type === 'image'">
                  <input
                    [attr.id]="'overview-image-input-' + module.id"
                    type="file"
                    accept="image/*"
                    hidden
                    (change)="onOverviewImageModuleSelected(module.id, $event)" />
                  <div
                    class="module-media module-image-dropzone"
                    [class.drag-over]="overviewImageDragModuleId === module.id"
                    (click)="triggerOverviewImageUpload(module.id)"
                    (dragover)="onOverviewImageModuleDragOver(module.id, $event)"
                    (dragleave)="onOverviewImageModuleDragLeave(module.id, $event)"
                    (drop)="onOverviewImageModuleDrop(module.id, $event)">
                    <img *ngIf="module.assetUrl" [src]="module.assetUrl" alt="" />
                    <div *ngIf="!module.assetUrl" class="module-image-placeholder">
                      <span class="icon">🖼️</span>
                      <strong>Drag & drop or click to add image.</strong>
                      <small>JPEG, PNG, no larger than 4MB.</small>
                    </div>
                  </div>
                </ng-container>

                <ng-container *ngIf="module.type === 'video'">
                  <label class="overview-video-link-field">
                    Video Link *
                    <input
                      type="url"
                      [ngModel]="module.videoUrl"
                      (ngModelChange)="onOverviewVideoUrlInput(module.id, $event)"
                      placeholder="https://..." />
                  </label>
                  <div class="module-media" *ngIf="module.assetUrl">
                    <video [src]="module.assetUrl" controls preload="metadata"></video>
                  </div>
                </ng-container>
              </article>
            </div>

            <div class="overview-actions">
              <button type="button" class="btn" (click)="addTextModule()">Add text</button>
              <button type="button" class="btn" (click)="addImageModule()" [disabled]="uploadingOverviewMedia || overviewImageCount >= OVERVIEW_MAX_IMAGES">
                Add image
              </button>
              <button type="button" class="btn" (click)="addVideoModule()" [disabled]="uploadingOverviewMedia || overviewVideoCount >= OVERVIEW_MAX_VIDEOS">
                Add video
              </button>
            </div>
            <p class="hint" *ngIf="uploadingOverviewMedia">Uploading overview media...</p>
          </ng-container>

          <ng-template #overviewCollapsedView>
            <p class="overview-collapsed-preview">{{ overviewCollapsedPreview }}</p>
          </ng-template>
        </article>

        <article
          #goodToKnowPanel
          class="panel good-to-know-panel"
          [class.good-to-know-collapsed]="goodToKnowCollapsed"
          (click)="expandGoodToKnow()">
          <div class="good-to-know-head">
            <h2>Good to know</h2>
            <button
              type="button"
              class="good-to-know-plus"
              [class.done]="isGoodToKnowComplete"
              aria-label="Add good to know"
              (click)="openHighlightsModal($event)">
              {{ isGoodToKnowComplete ? '✓' : '+' }}
            </button>
          </div>
          <ng-container *ngIf="!goodToKnowCollapsed; else goodToKnowCollapsedView">
            <section class="good-to-know-group">
              <h3>Highlights</h3>
              <div class="good-to-know-chips">
                <button type="button" (click)="openHighlightsModal($event)">{{ highlightDressCode ? ('Dress code: ' + highlightDressCodeLabel) : '+ Add Dress code' }}</button>
                <button type="button" (click)="openHighlightsModal($event)">{{ highlightDoorTimeLabel }}</button>
                <button type="button" (click)="openHighlightsModal($event)">{{ highlightParking ? ('Parking: ' + highlightParkingLabel) : '+ Add Parking information' }}</button>
              </div>
            </section>
            <section class="good-to-know-group">
              <h3>Frequently asked questions</h3>
              <p class="good-to-know-tip">💡 Events with FAQs have 8% more organic traffic</p>
              <section class="faq-editor" *ngIf="faqItems.length > 0">
                <p class="hint faq-intro">Answer questions your attendees may have about the event, like accessibility and amenities.</p>
                <article class="faq-item" *ngFor="let faq of faqItems">
                  <label>
                    Question *
                    <input [(ngModel)]="faq.question" (ngModelChange)="onGoodToKnowFieldChange()" placeholder="Enter question" />
                  </label>
                  <label>
                    Answer *
                    <textarea rows="4" maxlength="300" [(ngModel)]="faq.answer" (ngModelChange)="onGoodToKnowFieldChange()" placeholder="Enter answer"></textarea>
                  </label>
                  <div class="faq-meta-row">
                    <span>{{ faq.answer.length }}/300</span>
                    <button type="button" class="link danger" (click)="removeFaqItem(faq.id, $event)">Delete</button>
                  </div>
                </article>
              </section>
              <button type="button" class="good-to-know-link" (click)="addFaqItem($event)">+ Add question</button>
            </section>
          </ng-container>
          <ng-template #goodToKnowCollapsedView>
            <section class="good-to-know-collapsed-view">
              <section class="good-to-know-group">
                <h3>Highlights</h3>
                <div class="good-to-know-chips">
                  <button type="button">{{ highlightDressCode ? ('Dress code: ' + highlightDressCodeLabel) : '+ Add Dress code' }}</button>
                  <button type="button">{{ highlightDoorTimeLabel }}</button>
                  <button type="button">{{ highlightParking ? ('Parking: ' + highlightParkingLabel) : '+ Add Parking information' }}</button>
                </div>
              </section>

              <section class="good-to-know-group">
                <h3>Frequently asked questions</h3>
                <section class="faq-collapsed-list" *ngIf="faqItems.length > 0; else noFaqCollapsedState">
                  <article class="faq-collapsed-item" *ngFor="let faq of faqItems">
                    <ng-container *ngIf="faq.question.trim() || faq.answer.trim()">
                      <p class="faq-collapsed-question">{{ faq.question || 'Untitled question' }}</p>
                      <p class="faq-collapsed-answer">{{ faq.answer || 'No answer yet' }}</p>
                    </ng-container>
                  </article>
                </section>
                <ng-template #noFaqCollapsedState>
                  <p class="hint">No questions added yet.</p>
                </ng-template>
              </section>
            </section>
          </ng-template>
        </article>

        <div class="modal-backdrop" *ngIf="highlightsModalOpen" (click)="closeHighlightsModal()">
          <article class="highlights-modal" (click)="$event.stopPropagation()">
            <button type="button" class="highlights-close" (click)="closeHighlightsModal()" aria-label="Close">×</button>
            <h2>Add highlights about your event</h2>

            <section class="highlights-section">
              <h3>What time can attendees check in before the event?</h3>
              <div class="highlights-time-row">
                <label class="highlights-time-field">
                  Time before event starts
                  <input type="number" min="0" [(ngModel)]="highlightDoorTimeValue" />
                </label>
                <div class="highlights-toggle">
                  <button type="button" [class.active]="highlightDoorTimeUnit === 'minutes'" (click)="highlightDoorTimeUnit = 'minutes'">Minutes</button>
                  <button type="button" [class.active]="highlightDoorTimeUnit === 'hours'" (click)="highlightDoorTimeUnit = 'hours'">Hours</button>
                </div>
              </div>
            </section>

            <section class="highlights-section">
              <h3>Dress code</h3>
              <div class="highlights-options">
                <button type="button" [class.active]="highlightDressCode === 'jci_polo'" (click)="highlightDressCode = 'jci_polo'">JCI Polo</button>
                <button type="button" [class.active]="highlightDressCode === 'smart_casual'" (click)="highlightDressCode = 'smart_casual'">Smart Casual</button>
                <button type="button" [class.active]="highlightDressCode === 'business_attire'" (click)="highlightDressCode = 'business_attire'">Business Attire</button>
                <button type="button" [class.active]="highlightDressCode === 'black_tie'" (click)="highlightDressCode = 'black_tie'">Black Tie</button>
              </div>
            </section>

            <section class="highlights-section">
              <h3>Is there parking at your venue?</h3>
              <div class="highlights-options">
                <button type="button" [class.active]="highlightParking === 'free'" (click)="highlightParking = 'free'">Free parking</button>
                <button type="button" [class.active]="highlightParking === 'paid'" (click)="highlightParking = 'paid'">Paid parking</button>
                <button type="button" [class.active]="highlightParking === 'none'" (click)="highlightParking = 'none'">No parking options</button>
              </div>
            </section>

            <div class="highlights-actions">
              <button type="button" class="btn btn-primary" (click)="applyHighlightsModal()">Add to event</button>
            </div>
          </article>
        </div>
      </section>

      <section *ngIf="step === 2" class="ticket-layout">
        <article class="panel">
          <div class="panel-actions">
            <h2>Ticket</h2>
            <button type="button" class="btn" (click)="addTicket()">+ Add ticket</button>
          </div>

          <div class="ticket-tabs" *ngIf="tickets.length > 1">
            <button
              type="button"
              *ngFor="let ticket of tickets; let i = index"
              [class.active]="selectedTicketIndex === i"
              (click)="selectedTicketIndex = i">
              {{ ticket.name || ('Ticket ' + (i + 1)) }}
            </button>
          </div>

          <div class="panel-actions sub" *ngIf="tickets.length > 1">
            <h3>Editing: {{ currentTicket.name || ('Ticket ' + (selectedTicketIndex + 1)) }}</h3>
            <button type="button" class="link" (click)="removeSelectedTicket()">Remove</button>
          </div>

          <div class="field-grid">
            <label>
              Ticket name
              <input [(ngModel)]="currentTicket.name" />
            </label>
            <label>
              Description
              <textarea rows="2" [(ngModel)]="currentTicket.description"></textarea>
            </label>
          </div>

          <h3 class="subhead">Pricing method</h3>
          <div class="pricing-grid simple">
            <label class="type-card" [class.selected]="currentTicket.pricingMethod === 'fixed'">
              <input type="radio" name="pricingMethod" value="fixed" [(ngModel)]="currentTicket.pricingMethod" />
              <div><strong>Fixed price</strong><p>Charge everyone the same amount.</p></div>
            </label>
            <label class="type-card" [class.selected]="currentTicket.pricingMethod === 'free'">
              <input type="radio" name="pricingMethod" value="free" [(ngModel)]="currentTicket.pricingMethod" />
              <div><strong>Free</strong><p>Guests can get this ticket for free.</p></div>
            </label>
          </div>

          <div class="field-grid two-col">
            <label>
              Ticket price
              <input type="number" [(ngModel)]="currentTicket.price" [disabled]="currentTicket.pricingMethod === 'free'" />
            </label>
          </div>

          <h3 class="subhead">Ticket quantity</h3>
          <div class="chip-row two-up">
            <button type="button" class="choice" [class.active]="currentTicket.quantityMode === 'unlimited'" (click)="currentTicket.quantityMode = 'unlimited'">Unlimited number of tickets</button>
            <button type="button" class="choice" [class.active]="currentTicket.quantityMode === 'limited'" (click)="currentTicket.quantityMode = 'limited'">Limited number of tickets</button>
          </div>

          <div class="field-grid three-col">
            <label>
              Quantity total
              <input type="number" [(ngModel)]="currentTicket.quantityTotal" [disabled]="currentTicket.quantityMode === 'unlimited'" />
            </label>
            <label>
              Max qty per order
              <input type="number" [(ngModel)]="currentTicket.maxQtyPerOrder" />
            </label>
            <label>
              Visibility
              <select [(ngModel)]="currentTicket.visibility">
                <option value="show">Show ticket</option>
                <option value="hide">Hide ticket</option>
              </select>
            </label>
          </div>

          <label>
            Ticket description
            <textarea rows="3" [(ngModel)]="currentTicket.descriptionFooter"></textarea>
          </label>
        </article>

        <article class="panel ticket-assist">
          <div class="ticket-preview">
            <img *ngIf="imagePreviewUrl" [src]="imagePreviewUrl" alt="Ticket art" />
            <p *ngIf="!imagePreviewUrl">Upload event image to preview ticket design.</p>
          </div>
          <div class="help-list">
            <h4>Ticket setup tips</h4>
            <a href="#" (click)="$event.preventDefault()">Learn about Check-in app</a>
            <a href="#" (click)="$event.preventDefault()">Download Check-in app</a>
          </div>
        </article>
      </section>

      <section *ngIf="step === 3" class="step-layout">
        <article class="panel">
          <div class="panel-actions"><h2>Checkout form</h2></div>
          <div class="setting-row">
            <span>Guests are required to fill</span>
            <select [(ngModel)]="settings.formMode">
              <option value="per_order">1 form per order</option>
              <option value="per_attendee">1 form per attendee</option>
            </select>
          </div>
          <div class="setting-row wrap">
            <span>Form fields</span>
            <div class="checkboxes">
              <label><input type="checkbox" [(ngModel)]="checkoutFields.firstName" /> First name</label>
              <label><input type="checkbox" [(ngModel)]="checkoutFields.lastName" /> Last name</label>
              <label><input type="checkbox" [(ngModel)]="checkoutFields.email" /> Email</label>
              <label><input type="checkbox" [(ngModel)]="checkoutFields.phone" /> Phone</label>
            </div>
          </div>
        </article>

        <article class="panel">
          <div class="panel-actions"><h2>Ticket settings</h2></div>
          <div class="setting-row">
            <span>Currency</span>
            <input [(ngModel)]="settings.currency" />
          </div>
          <div class="setting-row">
            <span>Taxes</span>
            <select [(ngModel)]="settings.taxMode">
              <option value="none">Not added</option>
              <option value="excluded">Excluded</option>
              <option value="included">Included</option>
            </select>
          </div>
          <div class="setting-row">
            <span>Ticket limit per order</span>
            <input type="number" [(ngModel)]="settings.maxTicketsPerOrder" />
          </div>
          <div class="setting-row">
            <span>Checkout time limit</span>
            <input type="number" [(ngModel)]="settings.checkoutTimeLimitMinutes" />
          </div>
        </article>

        <article class="panel">
          <div class="panel-actions"><h2>Registration settings</h2></div>
          <div class="setting-row">
            <span>Registration visibility</span>
            <select [(ngModel)]="settings.registrationVisibility">
              <option value="public">Public</option>
              <option value="members_only">Members only</option>
              <option value="invite_only">Invite only</option>
            </select>
          </div>
          <div class="setting-row wrap">
            <span>Access</span>
            <div class="checkboxes">
              <label><input type="checkbox" [(ngModel)]="settings.requireLogin" /> Require login</label>
              <label><input type="checkbox" [(ngModel)]="settings.allowGuestCheckout" /> Allow guest checkout</label>
            </div>
          </div>
        </article>
      </section>

      <section *ngIf="step === 4" class="step-layout">
        <article class="panel">
          <h2>Features you can add</h2>
          <div class="lineup-feature-card">
            <div>
              <h3>Lineup</h3>
              <p>Add speakers or special guests for this event.</p>
              <p class="hint" *ngIf="lineupSpeakers.length > 0">{{ lineupSpeakers.length }} speaker(s) added</p>
            </div>
            <button type="button" class="btn btn-primary" (click)="openLineupModal()">Add</button>
          </div>
          <div class="feature-grid">
            <label class="feature-card" *ngFor="let feature of features">
              <input type="checkbox" [(ngModel)]="feature.selected" />
              <div>
                <strong>{{ feature.name }}</strong>
                <p>{{ feature.description || feature.key }}</p>
                <span>Add to event</span>
              </div>
            </label>
          </div>
        </article>

        <article class="panel">
          <h2>Publishing</h2>
          <label class="publish-row">
            <input type="checkbox" [(ngModel)]="publishOnFinish" />
            Publish event after creation
          </label>
          <p class="hint">When disabled, event is created as draft.</p>
        </article>
      </section>

      <div class="modal-backdrop" *ngIf="lineupModalOpen" (click)="cancelLineupModal()">
        <article class="lineup-modal" (click)="$event.stopPropagation()">
          <header class="lineup-modal-head">
            <h2>Lineup</h2>
            <button type="button" class="highlights-close" (click)="cancelLineupModal()" aria-label="Close">×</button>
          </header>
          <p class="hint">Add speakers or special guests and save when done.</p>
          <input #lineupImageInput type="file" accept="image/*" hidden (change)="onLineupImageSelected($event)" />

          <div class="lineup-editor-list">
            <article class="lineup-speaker-card" *ngFor="let speaker of lineupDraftSpeakers; let i = index">
              <div class="lineup-speaker-media">
                <button
                  type="button"
                  class="lineup-speaker-image"
                  [class.empty]="!speaker.fileId"
                  (click)="openLineupImagePicker(speaker.id, lineupImageInput)"
                >
                  <img *ngIf="speaker.fileId" [src]="displayLineupImageUrl(speaker)" alt="" />
                  <ng-container *ngIf="!speaker.fileId">
                    <span class="icon">🖼</span>
                    <strong>Upload an image*</strong>
                    <small>Image should be at least 320x320 pixels</small>
                    <span class="btn">Upload</span>
                  </ng-container>
                </button>
                <button
                  *ngIf="speaker.fileId"
                  type="button"
                  class="btn small"
                  (click)="openLineupImageCropper(speaker.id)"
                >
                  Crop
                </button>
              </div>
              <div class="lineup-speaker-fields">
                <label>
                  Name
                  <input [(ngModel)]="speaker.name" placeholder="Name" />
                </label>
                <label>
                  Tagline
                  <input [(ngModel)]="speaker.tagline" placeholder="Add a tagline" />
                </label>
                <label>
                  Description
                  <textarea rows="4" maxlength="5000" [(ngModel)]="speaker.description" placeholder="Description"></textarea>
                </label>
                <div class="faq-meta-row">
                  <span>{{ speaker.description.length }}/5000</span>
                  <button type="button" class="link danger" (click)="removeLineupDraftSpeaker(speaker.id)">Delete</button>
                </div>
              </div>
            </article>
          </div>

          <div class="lineup-add-row">
            <button type="button" class="btn" (click)="addLineupDraftSpeaker()">+ Add another</button>
          </div>
          <footer class="cropper-actions">
            <button type="button" class="btn" (click)="cancelLineupModal()">Cancel</button>
            <button type="button" class="btn btn-primary" (click)="saveLineupModal()">Save</button>
          </footer>
        </article>
      </div>

      <div class="cropper-backdrop" *ngIf="lineupCropDialogOpen">
        <article class="cropper-dialog" (click)="$event.stopPropagation()">
          <header class="cropper-head">
            <h3>Crop image</h3>
          </header>
          <div class="cropper-stage">
            <div class="cropper-frame" [class.dragging]="lineupCropDragActive">
              <img
                *ngIf="lineupCropDialogImageUrl"
                [src]="lineupCropDialogImageUrl"
                alt=""
                class="cropper-preview-image"
                [style.object-fit]="'contain'"
              />
              <div
                class="cropper-selection"
                [style.left.%]="lineupCropBoxX"
                [style.top.%]="lineupCropBoxY"
                [style.width.%]="lineupCropBoxWidth"
                [style.height.%]="lineupCropBoxHeight"
                (pointerdown)="onLineupCropSelectionPointerDown($event)"
              >
                <button type="button" class="cropper-handle cropper-handle-nw" (pointerdown)="onLineupCropResizeHandlePointerDown($event, 'nw')"></button>
                <button type="button" class="cropper-handle cropper-handle-ne" (pointerdown)="onLineupCropResizeHandlePointerDown($event, 'ne')"></button>
                <button type="button" class="cropper-handle cropper-handle-sw" (pointerdown)="onLineupCropResizeHandlePointerDown($event, 'sw')"></button>
                <button type="button" class="cropper-handle cropper-handle-se" (pointerdown)="onLineupCropResizeHandlePointerDown($event, 'se')"></button>
              </div>
            </div>
          </div>
          <footer class="cropper-actions">
            <button type="button" class="btn" (click)="cancelLineupImageCropper()">Cancel</button>
            <button type="button" class="btn btn-primary" (click)="applyLineupImageCropper()">Save</button>
          </footer>
        </article>
      </div>

      <footer class="wizard-footer">
        <button type="button" class="btn btn-primary" (click)="nextOrSubmit()" [disabled]="submitting || uploadingImage || uploadingSlideMedia || uploadingOverviewMedia || searchingLocation">
          {{ submitting ? 'Saving...' : (isEditMode ? 'Save changes' : 'Save event details') }}
        </button>
      </footer>

      <div class="cropper-backdrop" *ngIf="cropDialogOpen">
        <article class="cropper-dialog" (click)="$event.stopPropagation()">
          <header class="cropper-head">
            <h3>Crop image</h3>
          </header>
          <div class="cropper-stage">
            <div
              class="cropper-frame"
              [class.dragging]="cropDragActive"
            >
              <img
                *ngIf="cropDialogImageUrl"
                [src]="cropDialogImageUrl"
                alt=""
                class="cropper-preview-image"
                [style.object-fit]="'contain'"
              />
              <div
                class="cropper-selection"
                [style.left.%]="cropBoxX"
                [style.top.%]="cropBoxY"
                [style.width.%]="cropBoxWidth"
                [style.height.%]="cropBoxHeight"
                (pointerdown)="onCropSelectionPointerDown($event)"
              >
                <button
                  type="button"
                  class="cropper-handle cropper-handle-nw"
                  aria-label="Resize crop area"
                  (pointerdown)="onCropResizeHandlePointerDown($event, 'nw')"
                ></button>
                <button
                  type="button"
                  class="cropper-handle cropper-handle-ne"
                  aria-label="Resize crop area"
                  (pointerdown)="onCropResizeHandlePointerDown($event, 'ne')"
                ></button>
                <button
                  type="button"
                  class="cropper-handle cropper-handle-sw"
                  aria-label="Resize crop area"
                  (pointerdown)="onCropResizeHandlePointerDown($event, 'sw')"
                ></button>
                <button
                  type="button"
                  class="cropper-handle cropper-handle-se"
                  aria-label="Resize crop area"
                  (pointerdown)="onCropResizeHandlePointerDown($event, 'se')"
                ></button>
              </div>
            </div>
          </div>
          <div class="cropper-controls">
            <p class="hint">Drag to move. Resize using the corner handle. Aspect ratio is fixed at 2:1.</p>
          </div>
          <footer class="cropper-actions">
            <button type="button" class="btn" (click)="cancelImageCropper()">Cancel</button>
            <button type="button" class="btn btn-primary" (click)="applyImageCropper()">Apply crop</button>
          </footer>
        </article>
      </div>
    </section>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .wizard-shell {
        max-width: 1280px;
        margin: 0 auto;
        display: grid;
        gap: 0.95rem;
      }
      .wizard-header {
        background: #ffffff;
        border: 1px solid #dbe3ee;
        border-radius: 12px;
        padding: 0.9rem 1rem;
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
        gap: 0.8rem;
      }
      .eyebrow {
        margin: 0;
        color: #64748b;
        font-size: 0.74rem;
        letter-spacing: 0.08em;
        text-transform: uppercase;
      }
      h1 {
        margin: 0.15rem 0 0;
        font-size: 2rem;
        color: #0f172a;
      }
      .header-actions {
        display: flex;
        gap: 0.5rem;
      }
      .step-nav {
        display: flex;
        flex-wrap: wrap;
        gap: 0.45rem;
      }
      .step-nav button {
        border: 1px solid #dbe3ee;
        background: #ffffff;
        color: #334155;
        border-radius: 999px;
        padding: 0.45rem 0.78rem;
        display: flex;
        align-items: center;
        gap: 0.45rem;
        font-weight: 600;
        cursor: pointer;
      }
      .step-nav button span {
        width: 20px;
        height: 20px;
        border-radius: 999px;
        background: #eff6ff;
        color: #1d4ed8;
        display: grid;
        place-items: center;
        font-size: 0.75rem;
      }
      .step-nav button.active {
        border-color: #93c5fd;
        background: #dbeafe;
        color: #1e3a8a;
      }
      .step-layout {
        display: grid;
        gap: 0.9rem;
      }
      .panel {
        background: #ffffff;
        border: 1px solid #dbe3ee;
        border-radius: 12px;
        overflow: hidden;
        padding: 1rem;
      }
      .location-panel {
        overflow: visible;
        position: relative;
        z-index: 20;
      }
      .good-to-know-panel {
        border-color: #d7dce6;
      }
      .good-to-know-head {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      .good-to-know-plus {
        width: 36px;
        height: 36px;
        border-radius: 999px;
        border: 0;
        background: #edf4ff;
        color: #2563eb;
        font-size: 2rem;
        line-height: 1;
        cursor: pointer;
      }
      .good-to-know-plus.done {
        background: #34d399;
        color: #ffffff;
        font-size: 1.35rem;
        font-weight: 700;
      }
      .good-to-know-panel.good-to-know-collapsed {
        cursor: pointer;
      }
      .good-to-know-group {
        margin-top: 1.25rem;
      }
      .good-to-know-group h3 {
        margin: 0;
        color: #3f3c4e;
      }
      .good-to-know-chips {
        margin-top: 0.75rem;
        display: flex;
        flex-wrap: wrap;
        gap: 0.55rem;
      }
      .good-to-know-chips button {
        border: 1px solid #3f3c4e;
        background: #ffffff;
        color: #2f2c3d;
        border-radius: 9px;
        padding: 0.42rem 0.7rem;
        font-size: 0.93rem;
        cursor: pointer;
      }
      .good-to-know-tip {
        margin: 0.8rem 0 0;
        color: #4b5563;
      }
      .good-to-know-link {
        margin-top: 0.9rem;
        padding: 0;
        border: 0;
        background: transparent;
        color: #2563eb;
        font-size: 1.02rem;
        font-weight: 700;
        cursor: pointer;
      }
      .faq-editor {
        margin-top: 1rem;
        display: grid;
        gap: 0.8rem;
      }
      .faq-intro {
        margin: 0;
      }
      .faq-item {
        border: 1px solid #dbe3ee;
        border-radius: 10px;
        padding: 0.85rem;
        display: grid;
        gap: 0.55rem;
      }
      .faq-meta-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        color: #64748b;
        font-size: 0.84rem;
      }
      .good-to-know-collapsed-view {
        display: grid;
        gap: 1rem;
      }
      .faq-collapsed-list {
        display: grid;
        gap: 0.9rem;
      }
      .faq-collapsed-item {
        border-top: 1px solid #e2e8f0;
        padding-top: 0.8rem;
      }
      .faq-collapsed-question {
        margin: 0;
        color: #1f2937;
        font-weight: 700;
      }
      .faq-collapsed-answer {
        margin: 0.55rem 0 0;
        background: #f1f5f9;
        border-radius: 10px;
        color: #334155;
        padding: 0.7rem 0.8rem;
        white-space: pre-wrap;
      }
      .modal-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(31, 41, 55, 0.46);
        z-index: 80;
        display: grid;
        place-items: center;
        padding: 1.2rem;
      }
      .highlights-modal {
        width: min(960px, 100%);
        max-height: calc(100vh - 2.4rem);
        overflow: auto;
        background: #ffffff;
        border-radius: 12px;
        border: 1px solid #dbe3ee;
        padding: 1.35rem 1.5rem;
        position: relative;
      }
      .highlights-close {
        position: absolute;
        top: 0.8rem;
        right: 0.9rem;
        border: 0;
        background: transparent;
        color: #374151;
        font-size: 1.6rem;
        line-height: 1;
        cursor: pointer;
      }
      .highlights-modal h2 {
        margin: 0;
        font-size: 2rem;
        color: #2f2b45;
      }
      .highlights-section {
        margin-top: 1.7rem;
        display: grid;
        gap: 0.8rem;
      }
      .highlights-section h3 {
        margin: 0;
        color: #2f2b45;
        font-size: 1.05rem;
      }
      .highlights-time-row {
        display: flex;
        gap: 0.8rem;
        flex-wrap: wrap;
        align-items: flex-end;
      }
      .highlights-time-field {
        min-width: 240px;
      }
      .highlights-toggle {
        display: inline-grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        border: 1px solid #dbe3ee;
        border-radius: 10px;
        overflow: hidden;
      }
      .highlights-toggle button {
        border: 0;
        background: #f8fafc;
        color: #334155;
        padding: 0.58rem 0.9rem;
        font-weight: 700;
        cursor: pointer;
      }
      .highlights-toggle button.active {
        background: #2563eb;
        color: #ffffff;
      }
      .highlights-options {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 0.72rem;
      }
      .highlights-options button {
        border: 1px solid #d1d5db;
        background: #ffffff;
        color: #374151;
        border-radius: 10px;
        padding: 0.82rem 0.92rem;
        text-align: left;
        font-weight: 600;
        cursor: pointer;
      }
      .highlights-options button.active {
        border-color: #2563eb;
        background: #eff6ff;
        color: #1e40af;
      }
      .highlights-actions {
        margin-top: 1.8rem;
        display: flex;
        justify-content: center;
      }
      .overview-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
      .overview-complete-badge {
        width: 38px;
        height: 38px;
        border-radius: 999px;
        background: #34d399;
        color: #ffffff;
        display: grid;
        place-items: center;
        font-size: 1.3rem;
        font-weight: 700;
      }
      .overview-panel.overview-collapsed {
        cursor: pointer;
      }
      .date-location-panel {
        display: grid;
        gap: 0.75rem;
      }
      .date-location-panel.date-location-collapsed {
        cursor: pointer;
      }
      .date-location-collapsed-body {
        margin-top: 0.2rem;
      }
      .date-location-collapsed-grid {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 1px minmax(0, 1.2fr);
        gap: 1rem;
        align-items: start;
      }
      .date-location-collapsed-col h3 {
        margin: 0 0 0.45rem;
        font-size: 1.02rem;
        color: #312e45;
      }
      .date-location-collapsed-divider {
        background: #d6dde9;
        width: 1px;
        min-height: 72px;
      }
      .date-location-collapsed-line {
        margin: 0;
        color: #1f2937;
        line-height: 1.45;
        font-weight: 600;
      }
      .date-location-collapsed-address {
        margin: 0.38rem 0 0;
        color: #334155;
        line-height: 1.45;
      }
      .date-location-collapsed-map {
        margin-top: 0.85rem;
        height: 240px;
      }
      .overview-collapsed-preview {
        margin: 0.95rem 0 0;
        color: #4b5563;
        line-height: 1.8;
        white-space: pre-line;
      }
      .panel h2 {
        margin: 0 0 0.85rem;
        font-size: 1.15rem;
        color: #0f172a;
      }
      .panel h3 {
        margin: 0;
        color: #0f172a;
      }
      .subhead {
        margin: 1rem 0 0.65rem;
        font-size: 1.02rem;
      }
      .split-grid {
        display: grid;
        grid-template-columns: 2fr 1fr;
        gap: 0.9rem;
      }
      .field-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 0.75rem;
      }
      .four-col {
        grid-template-columns: repeat(4, minmax(0, 1fr));
      }
      .three-col {
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }
      .two-col {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
      .span-2 {
        grid-column: span 2;
      }
      label {
        display: grid;
        gap: 0.34rem;
        font-size: 0.9rem;
        color: #334155;
        font-weight: 600;
      }
      input,
      select,
      textarea {
        border: 1px solid #bfdbfe;
        background: #ffffff;
        border-radius: 8px;
        padding: 0.52rem 0.62rem;
        font: inherit;
        color: #0f172a;
        font-weight: 400;
      }
      textarea {
        resize: vertical;
      }
      .date-input {
        color-scheme: light;
      }
      .type-grid,
      .pricing-grid,
      .feature-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 0.75rem;
      }
      .pricing-grid.simple {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        max-width: 760px;
      }
      .type-card,
      .feature-card {
        border: 1px solid #dbe3ee;
        border-radius: 10px;
        padding: 0.75rem;
        display: flex;
        gap: 0.55rem;
        align-items: flex-start;
        cursor: pointer;
      }
      .type-card.selected {
        border-color: #93c5fd;
        background: #dbeafe;
      }
      .type-card p,
      .feature-card p {
        margin: 0.2rem 0 0;
        color: #475569;
        font-size: 0.87rem;
        font-weight: 400;
      }
      .feature-card span {
        display: inline-block;
        margin-top: 0.35rem;
        color: #2563eb;
        font-size: 0.85rem;
        font-weight: 600;
      }
      .lineup-feature-card {
        border: 1px solid #dbe3ee;
        border-radius: 10px;
        padding: 0.8rem;
        margin-bottom: 0.75rem;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.8rem;
      }
      .lineup-feature-card h3 {
        margin: 0;
      }
      .lineup-feature-card p {
        margin: 0.35rem 0 0;
        color: #475569;
      }
      .lineup-modal {
        width: min(920px, 100%);
        max-height: calc(100vh - 2.4rem);
        overflow: auto;
        background: #ffffff;
        border-radius: 12px;
        border: 1px solid #dbe3ee;
        padding: 1.2rem 1.3rem;
      }
      .lineup-modal-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
      .lineup-editor-list {
        margin-top: 0.95rem;
        display: grid;
        gap: 0.9rem;
      }
      .lineup-speaker-card {
        border: 1px solid #dbe3ee;
        border-radius: 10px;
        padding: 0.8rem;
        display: grid;
        grid-template-columns: 260px minmax(0, 1fr);
        gap: 1rem;
      }
      .lineup-speaker-media {
        display: grid;
        justify-items: center;
        gap: 0.5rem;
      }
      .lineup-speaker-image {
        width: 240px;
        height: 240px;
        border-radius: 999px;
        border: 1px solid #dbe3ee;
        background: #f8fafc;
        overflow: hidden;
        display: grid;
        place-items: center;
        text-align: center;
        color: #334155;
        gap: 0.2rem;
        cursor: pointer;
        padding: 0.8rem;
      }
      .lineup-speaker-image.empty .btn {
        margin-top: 0.35rem;
      }
      .lineup-speaker-image img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 999px;
      }
      .lineup-speaker-fields {
        display: grid;
        gap: 0.55rem;
      }
      .lineup-add-row {
        margin-top: 0.95rem;
        display: flex;
        justify-content: center;
      }
      .hint {
        margin: 0.35rem 0 0;
        color: #64748b;
        font-size: 0.84rem;
      }
      .limit-breach {
        color: #b91c1c;
        font-weight: 700;
      }
      .inline-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 0.45rem;
        align-items: center;
      }
      .slides-panel {
        display: grid;
        gap: 0.7rem;
      }
      .media-hero-panel {
        padding: 0;
        border: 0;
        background: transparent;
        box-shadow: none;
      }
      .media-hero {
        width: min(100%, 720px);
        margin: 0 auto;
        border: 0;
        border-radius: 12px;
        aspect-ratio: 2 / 1;
        overflow: hidden;
        position: relative;
        cursor: pointer;
        background: transparent;
      }
      .media-hero-bg {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        background: #0f172a;
        filter: brightness(0.58);
      }
      .media-hero-bg-fallback {
        background:
          linear-gradient(120deg, rgba(51, 65, 85, 0.5), rgba(15, 23, 42, 0.7)),
          radial-gradient(circle at 25% 20%, #64748b 0%, transparent 35%),
          radial-gradient(circle at 80% 70%, #334155 0%, transparent 36%);
      }
      .media-hero-plus {
        position: absolute;
        top: 18px;
        right: 18px;
        width: 38px;
        height: 38px;
        border-radius: 999px;
        border: 1px solid #93c5fd;
        background: rgba(248, 250, 252, 0.95);
        color: #2563eb;
        font-size: 1.35rem;
        display: grid;
        place-items: center;
        font-weight: 500;
        padding: 0;
      }
      .media-hero-plus.done {
        background: #16a34a;
        border-color: #16a34a;
        color: #ffffff;
        font-size: 1rem;
        font-weight: 800;
      }
      .media-hero-cta {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        min-width: 138px;
        min-height: 138px;
        border-radius: 10px;
        border: 1px solid #cbd5e1;
        background: rgba(248, 250, 252, 0.95);
        color: #1d4ed8;
        display: grid;
        place-content: center;
        gap: 0.05rem;
        font-weight: 700;
        font-size: 0.68rem;
        line-height: 1.25;
        z-index: 1;
        text-align: center;
      }
      .media-hero-cta-icon {
        display: inline-grid;
        place-items: center;
        width: 28px;
        height: 28px;
        margin: 0 auto 0.25rem;
        border-radius: 999px;
        background: #e2e8f0;
        color: #2563eb;
        font-size: 1rem;
        font-weight: 800;
      }
      .slides-editor-panel {
        border-color: #2563eb;
      }
      .media-section + .media-section {
        border-top: 1px solid #e2e8f0;
        margin-top: 1rem;
        padding-top: 1rem;
      }
      .dropzone {
        border: 1px solid #e2e8f0;
        border-radius: 10px;
        background: #f8fafc;
        min-height: 120px;
        padding: 1rem;
        display: grid;
        place-items: center;
        gap: 0.55rem;
      }
      .dropzone.drag-over {
        border-color: #2563eb;
        background: #dbeafe;
      }
      .dropzone.compact {
        min-height: 88px;
      }
      .drop-title {
        margin: 0;
        font-weight: 700;
        color: #1e293b;
      }
      .image-library {
        display: grid;
        gap: 0.65rem;
      }
      .image-preview-card {
        border: 1px solid #e2e8f0;
        border-radius: 10px;
        overflow: hidden;
        background: #f8fafc;
        width: min(100%, 720px);
        aspect-ratio: 2 / 1;
        margin: 0 auto;
        display: grid;
        place-items: center;
        position: relative;
      }
      .image-preview-card img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
        display: block;
        background: #0f172a;
      }
      .image-preview-overlay {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 1rem;
        opacity: 0;
        pointer-events: none;
        background: rgba(15, 23, 42, 0.2);
        transition: opacity 0.18s ease;
      }
      .image-preview-card:hover .image-preview-overlay {
        opacity: 1;
      }
      .overlay-action {
        width: 54px;
        height: 54px;
        border-radius: 999px;
        border: 2px solid #ffffff;
        color: #ffffff;
        background: rgba(15, 23, 42, 0.32);
        cursor: pointer;
        display: grid;
        place-items: center;
        pointer-events: auto;
        box-shadow: 0 2px 10px rgba(2, 6, 23, 0.25);
      }
      .overlay-action.danger {
        background: rgba(15, 23, 42, 0.32);
      }
      .overlay-icon-crop {
        width: 24px;
        height: 24px;
        stroke: #ffffff;
        stroke-width: 1.6;
        fill: none;
        stroke-linecap: round;
        stroke-linejoin: round;
      }
      .overlay-icon-trash {
        width: 18px;
        height: 18px;
        stroke: #ffffff;
        stroke-width: 1.6;
        fill: none;
        stroke-linecap: round;
        stroke-linejoin: round;
      }
      .thumb-row {
        display: flex;
        flex-wrap: wrap;
        gap: 0.6rem;
        align-items: flex-start;
      }
      .thumb-entry {
        display: grid;
        gap: 0.18rem;
        transition: transform 140ms ease;
      }
      .thumb-actions {
        margin-top: 0.45rem;
      }
      .thumb-add,
      .thumb-item {
        width: 96px;
        aspect-ratio: 2 / 1;
        border-radius: 8px;
        border: 0;
        background: #ffffff;
        cursor: pointer;
        overflow: hidden;
      }
      .thumb-add {
        font-size: 2rem;
        color: #2563eb;
        line-height: 1;
      }
      .thumb-item img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
        display: block;
        background: transparent;
      }
      .thumb-item {
        cursor: grab;
      }
      .thumb-item.active {
        box-shadow: none;
      }
      .thumb-item.drag-target {
        outline: 2px solid #2563eb;
        outline-offset: 1px;
      }
      .thumb-item.dragging {
        opacity: 0.55;
        cursor: grabbing;
      }
      .thumb-cover-badge {
        font-size: 0.68rem;
        color: #16a34a;
        font-weight: 700;
        text-align: center;
      }
      .media-info {
        margin: 0.65rem 0 0;
        padding-left: 1rem;
        color: #475569;
        font-size: 0.86rem;
        display: flex;
        flex-wrap: wrap;
        gap: 1rem;
      }
      .slides-empty {
        border: 1px dashed #cbd5e1;
        border-radius: 10px;
        padding: 0.85rem;
        color: #64748b;
      }
      .slides-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 0.75rem;
      }
      .slide-item {
        border: 1px solid #dbe3ee;
        border-radius: 10px;
        overflow: hidden;
        background: #f8fafc;
      }
      .slide-item img,
      .slide-item video {
        width: 100%;
        height: 150px;
        object-fit: cover;
        display: block;
      }
      .slide-meta {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.5rem 0.6rem;
        font-size: 0.84rem;
      }
      .overview-panel {
        display: grid;
        gap: 0.75rem;
      }
      .module-list {
        display: grid;
        gap: 0.8rem;
      }
      .module-card {
        border: 1px solid #dbe3ee;
        border-radius: 10px;
        background: #f8fafc;
        padding: 0.75rem;
        display: grid;
        gap: 0.6rem;
      }
      .module-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem;
      }
      .module-media {
        border: 1px solid #dbe3ee;
        border-radius: 10px;
        overflow: hidden;
        background: #ffffff;
      }
      .module-media img,
      .module-media video {
        width: 100%;
        max-height: 300px;
        display: block;
      }
      .module-image-dropzone {
        min-height: 170px;
        display: grid;
        place-items: center;
        border-style: dashed;
        cursor: pointer;
      }
      .module-image-dropzone.drag-over {
        border-color: #2563eb;
        background: #eff6ff;
      }
      .module-image-placeholder {
        text-align: center;
        color: #334155;
        display: grid;
        gap: 0.3rem;
      }
      .module-image-placeholder .icon {
        font-size: 1.5rem;
        opacity: 0.75;
      }
      .module-image-placeholder small {
        color: #64748b;
      }
      .overview-video-link-field {
        display: grid;
        gap: 0.38rem;
      }
      .overview-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
      }
      .btn.small {
        padding: 0.25rem 0.55rem;
        font-size: 0.8rem;
      }
      .chip-row {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 0.65rem;
        margin-bottom: 0.8rem;
      }
      .chip-row.two-up {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
      .choice {
        border: 1px solid #dbe3ee;
        border-radius: 10px;
        background: #f8fafc;
        color: #1e293b;
        padding: 0.58rem;
        text-align: left;
        font-weight: 600;
        cursor: pointer;
      }
      .choice.active {
        border-color: #93c5fd;
        background: #dbeafe;
        color: #1e40af;
      }
      .online-info-box {
        border: 1px solid #dbe3ee;
        border-radius: 10px;
        background: #f8fafc;
        color: #334155;
        padding: 0.95rem 1rem;
        line-height: 1.45;
      }
      .google-places-host {
        width: 100%;
        position: relative;
        z-index: 30;
      }
      .google-places-host gmp-place-autocomplete {
        width: 100%;
        color-scheme: light;
        --gmpx-color-surface: #ffffff;
        --gmpx-color-on-surface: #0f172a;
        --gmpx-color-outline: #bfdbfe;
      }
      .google-places-host > * {
        width: 100%;
      }
      .map-preview {
        width: 100%;
        height: 220px;
        border: 1px solid #dbe3ee;
        border-radius: 10px;
      }
      .location-suggestions {
        margin: 0.45rem 0 0;
        padding: 0;
        list-style: none;
        border: 1px solid #dbe3ee;
        border-radius: 10px;
        background: #ffffff;
        max-height: 220px;
        overflow: auto;
      }
      .location-error {
        margin: 0.35rem 0 0;
        color: #b91c1c;
        font-size: 0.82rem;
        font-weight: 600;
      }
      .location-suggestions li + li {
        border-top: 1px solid #e2e8f0;
      }
      .location-suggestions button {
        border: 0;
        width: 100%;
        text-align: left;
        background: #ffffff;
        padding: 0.55rem 0.65rem;
        display: grid;
        gap: 0.18rem;
        cursor: pointer;
      }
      .location-suggestions button:hover {
        background: #f8fafc;
      }
      .location-suggestions strong {
        color: #0f172a;
      }
      .location-suggestions span {
        color: #64748b;
        font-size: 0.84rem;
      }
      .reset-location {
        justify-self: start;
        margin-top: 0.25rem;
      }
      .editor-toolbar {
        display: flex;
        flex-wrap: wrap;
        gap: 0.45rem;
        margin-bottom: 0.55rem;
      }
      .editor-toolbar button {
        border: 1px solid #cbd5e1;
        background: #ffffff;
        color: #1e293b;
        border-radius: 999px;
        padding: 0.28rem 0.62rem;
        cursor: pointer;
      }
      .rich-editor {
        border: 1px solid #bfdbfe;
        border-radius: 10px;
        min-height: 220px;
        padding: 0.75rem;
        outline: none;
      }
      .rich-editor:empty::before {
        content: attr(data-placeholder);
        color: #94a3b8;
      }
      .ticket-layout {
        display: grid;
        grid-template-columns: 1fr;
        gap: 0.9rem;
      }
      .ticket-assist {
        display: grid;
        grid-template-columns: 1.3fr 1fr;
        gap: 0.85rem;
        align-items: start;
      }
      .panel-actions {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.85rem;
      }
      .panel-actions.sub {
        margin-top: 0.25rem;
      }
      .ticket-tabs {
        display: flex;
        flex-wrap: wrap;
        gap: 0.45rem;
        margin-bottom: 0.75rem;
      }
      .ticket-tabs button {
        border: 1px solid #bfdbfe;
        border-radius: 999px;
        background: #ffffff;
        color: #1d4ed8;
        padding: 0.34rem 0.65rem;
        cursor: pointer;
      }
      .ticket-tabs button.active {
        background: #dbeafe;
        border-color: #93c5fd;
      }
      .side-panel {
        display: grid;
        gap: 1rem;
        align-content: start;
      }
      .ticket-preview {
        border: 1px solid #dbe3ee;
        border-radius: 10px;
        min-height: 230px;
        background: #f8fafc;
        display: grid;
        place-items: center;
        padding: 0.75rem;
      }
      .ticket-preview img {
        max-width: 100%;
        border-radius: 8px;
      }
      .ticket-preview p {
        margin: 0;
        color: #64748b;
        text-align: center;
      }
      .help-list {
        border: 1px solid #dbe3ee;
        border-radius: 10px;
        padding: 0.85rem;
        display: grid;
        gap: 0.35rem;
      }
      .help-list h4 {
        margin: 0 0 0.35rem;
      }
      .help-list a {
        color: #2563eb;
        text-decoration: none;
        font-size: 0.9rem;
      }
      .setting-row {
        display: grid;
        grid-template-columns: 1fr 260px;
        gap: 0.65rem;
        align-items: center;
        padding: 0.68rem 0;
        border-top: 1px solid #e2e8f0;
      }
      .setting-row:first-of-type {
        border-top: 0;
      }
      .setting-row.wrap {
        grid-template-columns: 1fr;
      }
      .checkboxes {
        display: flex;
        flex-wrap: wrap;
        gap: 0.85rem;
      }
      .checkboxes label,
      .publish-row {
        display: flex;
        align-items: center;
        gap: 0.35rem;
        font-weight: 500;
      }
      .wizard-footer {
        display: flex;
        justify-content: space-between;
        gap: 0.5rem;
      }
      .btn {
        border: 1px solid #bfdbfe;
        background: #ffffff;
        color: #1d4ed8;
        border-radius: 999px;
        padding: 0.45rem 0.92rem;
        font-weight: 700;
        cursor: pointer;
      }
      .btn-primary {
        background: #2563eb;
        border-color: #2563eb;
        color: #ffffff;
      }
      .link {
        border: 0;
        background: transparent;
        color: #2563eb;
        cursor: pointer;
        font-weight: 700;
      }
      .link.danger {
        color: #b91c1c;
      }
      .error {
        margin: 0;
        color: #b91c1c;
        font-weight: 700;
      }
      .success {
        margin: 0;
        color: #166534;
        font-weight: 700;
      }
      .cropper-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(2, 6, 23, 0.6);
        display: grid;
        place-items: center;
        z-index: 1200;
        padding: 1rem;
      }
      .cropper-dialog {
        width: min(760px, 94vw);
        max-height: 92vh;
        overflow: auto;
        background: #ffffff;
        border: 1px solid #dbe3ee;
        border-radius: 12px;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        padding: 1rem;
      }
      .cropper-head h3 {
        margin: 0;
        text-align: center;
      }
      .cropper-stage {
        background: #eef2f7;
        border-radius: 10px;
        padding: 0.55rem;
        flex: 1 1 auto;
        min-height: 0;
      }
      .cropper-frame {
        position: relative;
        width: min(100%, 560px);
        margin: 0 auto;
        aspect-ratio: 1 / 1;
        height: auto;
        max-height: none;
        overflow: hidden;
        border-radius: 10px;
        border: 1px solid #cbd5e1;
        background: #a1a1ad;
        cursor: grab;
        touch-action: none;
      }
      .cropper-frame.dragging {
        cursor: grabbing;
      }
      .cropper-preview-image {
        width: 100%;
        height: 100%;
        object-fit: contain !important;
        object-position: center;
        transform-origin: center center;
        display: block;
        pointer-events: none;
      }
      .cropper-selection {
        position: absolute;
        box-shadow: 0 0 0 9999px rgba(148, 149, 162, 0.76);
        border: 1px dashed rgba(255, 255, 255, 0.75);
        cursor: move;
        touch-action: none;
      }
      .cropper-handle {
        position: absolute;
        width: 14px;
        height: 14px;
        border-radius: 2px;
        border: 1px solid rgba(255, 255, 255, 0.9);
        background: rgba(15, 23, 42, 0.55);
        padding: 0;
        cursor: nwse-resize;
      }
      .cropper-handle-se {
        right: -8px;
        bottom: -8px;
      }
      .cropper-handle-nw {
        left: -8px;
        top: -8px;
      }
      .cropper-handle-ne {
        right: -8px;
        top: -8px;
      }
      .cropper-handle-sw {
        left: -8px;
        bottom: -8px;
      }
      .cropper-controls {
        display: grid;
        gap: 0.35rem;
      }
      .cropper-controls label {
        display: grid;
        gap: 0.25rem;
      }
      .cropper-actions {
        display: flex;
        justify-content: flex-end;
        gap: 0.5rem;
        padding-top: 0.25rem;
        border-top: 1px solid #e2e8f0;
      }
      @media (max-width: 768px) {
        .wizard-header {
          flex-wrap: wrap;
          align-items: flex-start;
          padding: 0.75rem;
        }
        h1 {
          font-size: 1.35rem;
        }
        .header-actions {
          width: 100%;
          justify-content: flex-end;
        }
        .btn {
          padding: 0.4rem 0.8rem;
          font-size: 0.82rem;
        }
        .step-nav {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          padding-bottom: 2px;
        }
      }

      @media (max-width: 1080px) {
        .split-grid,
        .ticket-layout,
        .ticket-assist {
          grid-template-columns: 1fr;
        }
        .highlights-options {
          grid-template-columns: 1fr;
        }
        .date-location-collapsed-grid {
          grid-template-columns: 1fr;
          gap: 0.7rem;
        }
        .date-location-collapsed-divider {
          display: none;
        }
        .field-grid,
        .chip-row,
        .type-grid,
        .pricing-grid,
        .feature-grid,
        .slides-grid,
        .three-col,
        .four-col {
          grid-template-columns: 1fr;
        }
        .span-2 {
          grid-column: auto;
        }
        .setting-row {
          grid-template-columns: 1fr;
        }
      }
    `
  ]
})
export class EventCreateWizardComponent implements OnInit, OnDestroy {
  private static readonly ALLOWED_RETURN_TABS = new Set([
    'overview',
    'tickets-seating',
    'settings',
    'features',
    'orders',
    'guests'
  ]);
  readonly OVERVIEW_TEXT_MAX_CHARS = 2500;
  readonly OVERVIEW_MAX_IMAGES = 10;
  readonly OVERVIEW_MAX_VIDEOS = 3;
  readonly JAKARTA_TIMEZONE = 'Asia/Jakarta';
  readonly EVENT_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
  readonly EVENT_IMAGE_MIN_DIMENSION = 600;
  readonly EVENT_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png'];

  @ViewChild('googlePlacesHost')
  set googlePlacesHostSetter(el: ElementRef<HTMLDivElement> | undefined) {
    this.googlePlacesHostEl = el;
    if (el) void this.initGooglePlacesWidget();
  }
  @ViewChild('mediaEditorPanel') mediaEditorPanel?: ElementRef<HTMLElement>;
  @ViewChild('overviewPanel') overviewPanel?: ElementRef<HTMLElement>;
  @ViewChild('dateLocationPanel') dateLocationPanel?: ElementRef<HTMLElement>;
  @ViewChild('goodToKnowPanel') goodToKnowPanel?: ElementRef<HTMLElement>;

  readonly steps = ['Event details'];

  step = 1;
  submitting = false;
  uploadingImage = false;
  searchingLocation = false;
  error = '';
  success = '';
  selectedTicketIndex = 0;
  editEventId = '';
  returnTab = 'overview';
  private existingEventStatus = 'draft';

  details = {
    eventType: 'ticketed' as 'ticketed',
    eventTimeMode: 'single' as 'single' | 'recurring',
    title: '',
    categoryId: '',
    imageId: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    locationMode: 'physical' as 'physical' | 'online',
    location: '',
    locationAddress: '',
    onlineMeetingUrl: '',
    descriptionPublic: '',
    projectDirectorId: '',
    projectDirectorPhone: ''
  };

  locationSearch = '';
  locationSuggestions: LocationSuggestion[] = [];
  locationError = '';
  selectedLocation: LocationSelection | null = null;

  tickets: TicketDraft[] = [this.emptyTicket()];

  settings = {
    formMode: 'per_order' as 'per_order' | 'per_attendee',
    maxTicketsPerOrder: 50,
    currency: 'IDR',
    taxMode: 'none' as 'none' | 'excluded' | 'included',
    checkoutTimeLimitMinutes: 20,
    requireLogin: false,
    allowGuestCheckout: true,
    registrationVisibility: 'public' as 'public' | 'members_only' | 'invite_only'
  };

  checkoutFields = {
    firstName: true,
    lastName: true,
    email: true,
    phone: false
  };

  categories: CategoryOption[] = [];
  members: MemberOption[] = [];
  features: FeatureOption[] = [];
  readonly timeOptions: TimeOption[] = this.buildTimeOptions();
  publishOnFinish = true;
  imagePreviewUrl = '';
  uploadingSlideMedia = false;
  uploadingOverviewMedia = false;
  mediaEditorOpen = false;
  overviewCollapsed = false;
  dateLocationCollapsed = false;
  goodToKnowCollapsed = false;
  overviewImageDragModuleId = '';
  highlightsModalOpen = false;
  highlightDoorTimeValue: number | null = null;
  highlightDoorTimeUnit: 'minutes' | 'hours' = 'minutes';
  highlightDressCode: '' | 'jci_polo' | 'smart_casual' | 'business_attire' | 'black_tie' = '';
  highlightParking: '' | 'free' | 'paid' | 'none' = '';
  faqItems: FaqItem[] = [];
  lineupSpeakers: LineupSpeaker[] = [];
  lineupDraftSpeakers: LineupSpeaker[] = [];
  lineupModalOpen = false;
  lineupPendingImageTargetId = '';
  lineupCropDialogOpen = false;
  lineupCropDialogTargetSpeakerId = '';
  lineupCropDialogImageUrl = '';
  lineupCropDragActive = false;
  lineupCropBoxX = 0;
  lineupCropBoxY = 0;
  lineupCropBoxWidth = 100;
  lineupCropBoxHeight = 100;
  draggingThumbId = '';
  dragOverThumbId = '';
  slideImageDragOver = false;
  selectedImagePreviewId = '';
  cropDialogOpen = false;
  cropDialogTargetSlideId = '';
  cropDialogImageUrl = '';
  cropDraftX = 0;
  cropDraftY = 0;
  cropDraftZoom = 1;
  cropDragActive = false;
  cropBoxX = 0;
  cropBoxY = 0;
  cropBoxWidth = 100;
  cropBoxHeight = 50;
  private cropResizeMode: '' | 'move' | 'resize' = '';
  private cropResizeHandle: '' | 'nw' | 'ne' | 'sw' | 'se' = '';
  private cropDragStartX = 0;
  private cropDragStartY = 0;
  private cropDragOriginBoxX = 0;
  private cropDragOriginBoxY = 0;
  private cropDragOriginBoxWidth = 100;
  private cropDragOriginBoxHeight = 50;
  private cropDragFrameWidth = 1;
  private cropDragFrameHeight = 1;
  private lineupCropResizeMode: '' | 'move' | 'resize' = '';
  private lineupCropResizeHandle: '' | 'nw' | 'ne' | 'sw' | 'se' = '';
  private lineupCropDragStartX = 0;
  private lineupCropDragStartY = 0;
  private lineupCropDragOriginBoxX = 0;
  private lineupCropDragOriginBoxY = 0;
  private lineupCropDragOriginBoxWidth = 100;
  private lineupCropDragOriginBoxHeight = 100;
  private lineupCropDragFrameWidth = 1;
  private lineupCropDragFrameHeight = 1;
  eventSlides: EventSlide[] = [];
  overviewModules: OverviewModule[] = [this.createTextModule('')];
  googleMapEmbedUrlSafe: SafeResourceUrl | null = null;
  private googlePlacesHostEl?: ElementRef<HTMLDivElement>;
  private googlePlacesScriptPromise?: Promise<void>;
  private googlePlacesWidgetAttached = false;
  private locationSearchTimer: ReturnType<typeof setTimeout> | null = null;
  private locationRequestSeq = 0;
  private readonly originalEventMediaFileIds = new Set<string>();
  private readonly uploadedSlideMediaFileIds = new Set<string>();

  private readonly directusBase = environment.directusUrl.replace(/\/$/, '');

  constructor(
    private readonly directus: DirectusService,
    private readonly sanitizer: DomSanitizer,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {}

  get isEditMode(): boolean {
    return !!this.editEventId;
  }

  get hasAttachedMedia(): boolean {
    return this.eventSlides.length > 0;
  }

  get heroMediaBackgroundUrl(): string {
    if (this.coverImageSlide?.assetUrl) return this.displayImageUrl(this.coverImageSlide);
    if (this.hasAttachedMedia && this.imagePreviewUrl) return this.imagePreviewUrl;
    return '';
  }

  get imageSlides(): EventSlide[] {
    return this.eventSlides.filter((slide) => slide.type === 'image');
  }

  get videoSlides(): EventSlide[] {
    return this.eventSlides.filter((slide) => slide.type === 'video');
  }

  get previewImageSlide(): EventSlide | null {
    if (!this.imageSlides.length) return null;
    const selected = this.imageSlides.find((slide) => slide.id === this.selectedImagePreviewId);
    return selected || this.imageSlides[0];
  }

  get coverImageSlide(): EventSlide | null {
    return this.imageSlides[0] || null;
  }

  get coverImageFileId(): string {
    return this.coverImageSlide?.fileId || '';
  }

  displayImageUrl(slide: EventSlide | null | undefined): string {
    return slide?.croppedPreviewUrl || slide?.assetUrl || '';
  }

  imageObjectPosition(slide: EventSlide | null | undefined): string {
    if (slide?.croppedPreviewUrl) return '50% 50%';
    const boxWidth = Number(slide?.cropBoxWidth ?? 0);
    const boxHeight = Number(slide?.cropBoxHeight ?? 0);
    const boxX = Number(slide?.cropBoxX ?? 0);
    const boxY = Number(slide?.cropBoxY ?? 0);
    const hasBox = Number.isFinite(boxWidth)
      && Number.isFinite(boxHeight)
      && boxWidth > 0
      && boxHeight > 0
      && boxWidth <= 100
      && boxHeight <= 100;
    if (hasBox) {
      const x = Math.max(0, Math.min(100, boxX + boxWidth / 2));
      const y = Math.max(0, Math.min(100, boxY + boxHeight / 2));
      return `${x}% ${y}%`;
    }
    const legacyX = 50 + Number(slide?.cropX || 0);
    const legacyY = 50 + Number(slide?.cropY || 0);
    return `${legacyX}% ${legacyY}%`;
  }

  imageTransform(slide: EventSlide | null | undefined): string {
    if (slide?.croppedPreviewUrl) return 'scale(1)';
    const boxWidth = Number(slide?.cropBoxWidth ?? 0);
    const hasBox = Number.isFinite(boxWidth) && boxWidth > 0 && boxWidth <= 100;
    if (hasBox) {
      // Match viewer zoom to crop selector width (2:1 selector in a cover frame).
      const boxZoom = 100 / boxWidth;
      return `scale(${Math.min(6, Math.max(1, boxZoom))})`;
    }
    const legacyZoom = Number(slide?.cropZoom || 1);
    return `scale(${Math.min(6, Math.max(1, legacyZoom))})`;
  }

  get overviewTextCharCount(): number {
    return this.overviewModules
      .filter((module) => module.type === 'text')
      .reduce((sum, module) => sum + this.stripHtml(module.textHtml).length, 0);
  }

  overviewTextModuleCharCount(module: OverviewModule): number {
    if (module.type !== 'text') return 0;
    return this.stripHtml(module.textHtml).length;
  }

  get overviewImageCount(): number {
    return this.overviewModules.filter((module) => module.type === 'image').length;
  }

  get overviewVideoCount(): number {
    return this.overviewModules.filter((module) => module.type === 'video').length;
  }

  get isOverviewLimitExceeded(): boolean {
    return this.overviewModules.some(
      (module) => module.type === 'text' && this.overviewTextModuleCharCount(module) > this.OVERVIEW_TEXT_MAX_CHARS
    )
      || this.overviewImageCount > this.OVERVIEW_MAX_IMAGES
      || this.overviewVideoCount > this.OVERVIEW_MAX_VIDEOS;
  }

  get isOverviewComplete(): boolean {
    if (!this.overviewModules.length) return false;
    return this.overviewModules.every((module) => {
      if (module.type === 'text') {
        return this.stripHtml(module.textHtml).trim().length > 0;
      }
      if (module.type === 'image') return !!module.fileId.trim();
      return !!module.videoUrl.trim() || !!module.fileId.trim();
    });
  }

  get overviewCollapsedPreview(): string {
    const textParts = this.overviewModules
      .filter((module) => module.type === 'text')
      .map((module) => this.stripHtml(module.textHtml).trim())
      .filter((part) => !!part);
    if (textParts.length > 0) {
      return textParts.join('\n').slice(0, 320);
    }
    if (this.overviewImageCount > 0 || this.overviewVideoCount > 0) {
      return `${this.overviewImageCount} image(s) and ${this.overviewVideoCount} video(s) added.`;
    }
    return 'Click to edit overview.';
  }

  get isDateLocationComplete(): boolean {
    const hasDateTime = !!(
      this.details.startDate.trim()
      && this.details.startTime.trim()
      && this.details.endDate.trim()
      && this.details.endTime.trim()
    );
    if (!hasDateTime) return false;
    if (this.details.locationMode === 'online') return true;
    return !!(
      this.selectedLocation
      && this.selectedLocation.venueName.trim()
      && this.selectedLocation.address.trim()
    );
  }

  get dateLocationCollapsedPreview(): string {
    const modeLabel = this.details.eventTimeMode === 'recurring' ? 'Recurring event' : 'Single event';
    const dateLabel = this.details.startDate
      ? `${this.formatDateDisplay(this.details.startDate)} ${this.formatTimeDisplay(this.details.startTime)}`
      : 'Date/time not set';
    const locationLabel = this.details.locationMode === 'online'
      ? 'Online event page'
      : (this.selectedLocation?.venueName?.trim() || this.selectedLocation?.address?.trim() || 'Location not set');
    return `${modeLabel}\n${dateLabel}\n${locationLabel}`;
  }

  get dateLocationCollapsedDateTime(): string {
    const startDate = this.formatDateLongDisplay(this.details.startDate);
    const startTime = this.formatTimeDisplay(this.details.startTime);
    const endTime = this.formatTimeDisplay(this.details.endTime);
    if (!startDate) return 'Date/time not set';
    if (!startTime || !endTime) return `${startDate} ${startTime || ''}`.trim();
    return `${startDate} ${startTime} - ${endTime} WIB`;
  }

  get dateLocationCollapsedLocationTitle(): string {
    if (this.details.locationMode === 'online') return 'Online event';
    return this.selectedLocation?.venueName?.trim() || 'Location not set';
  }

  get dateLocationCollapsedLocationAddress(): string {
    if (this.details.locationMode === 'online') return 'Online event page will be used for livestream links and event access.';
    return this.selectedLocation?.address?.trim() || '';
  }

  get highlightDressCodeLabel(): string {
    if (this.highlightDressCode === 'jci_polo') return 'JCI Polo';
    if (this.highlightDressCode === 'smart_casual') return 'Smart Casual';
    if (this.highlightDressCode === 'business_attire') return 'Business Attire';
    if (this.highlightDressCode === 'black_tie') return 'Black Tie';
    return '';
  }

  get highlightDoorTimeLabel(): string {
    if (this.highlightDoorTimeValue === null || Number(this.highlightDoorTimeValue) <= 0) return '+ Add Door Time';
    const suffix = this.highlightDoorTimeUnit === 'hours' ? 'hour(s)' : 'minute(s)';
    return `Door Time: ${this.highlightDoorTimeValue} ${suffix}`;
  }

  get highlightParkingLabel(): string {
    if (this.highlightParking === 'free') return 'Free parking';
    if (this.highlightParking === 'paid') return 'Paid parking';
    if (this.highlightParking === 'none') return 'No parking options';
    return '';
  }

  get isGoodToKnowComplete(): boolean {
    const hasHighlight = !!(
      this.highlightDressCode
      || this.highlightParking
      || (this.highlightDoorTimeValue !== null && Number(this.highlightDoorTimeValue) > 0)
    );
    const hasFaq = this.faqItems.some((faq) => faq.question.trim().length > 0 || faq.answer.trim().length > 0);
    return hasHighlight || hasFaq;
  }

  get goodToKnowCollapsedPreview(): string {
    if (!this.isGoodToKnowComplete) return 'Click to add highlights and FAQs.';
    const parts: string[] = [];
    if (this.highlightDressCode) parts.push(`Dress code: ${this.highlightDressCodeLabel}`);
    if (this.highlightDoorTimeValue !== null && Number(this.highlightDoorTimeValue) > 0) parts.push(this.highlightDoorTimeLabel);
    if (this.highlightParking) parts.push(`Parking: ${this.highlightParkingLabel}`);
    const faqCount = this.faqItems.filter((faq) => faq.question.trim() || faq.answer.trim()).length;
    if (faqCount > 0) parts.push(`FAQs: ${faqCount}`);
    return parts.join('\n');
  }

  moduleLabel(type: OverviewModuleType): string {
    if (type === 'text') return 'Text';
    if (type === 'image') return 'Image';
    return 'Video';
  }

  async ngOnInit(): Promise<void> {
    this.googleMapEmbedUrlSafe = this.sanitizer.bypassSecurityTrustResourceUrl('about:blank');
    this.editEventId = String(this.route.snapshot.queryParamMap.get('eventId') || '').trim();
    this.returnTab = this.parseReturnTab(this.route.snapshot.queryParamMap.get('returnTab'));
    await Promise.all([this.loadCategories(), this.loadFeatures(), this.loadMembers()]);
    if (this.editEventId) {
      await this.loadEventForEdit();
    }
    void this.initGooglePlacesWidget();
  }

  ngOnDestroy(): void {
    this.revokePreviewUrl();
    if (this.locationSearchTimer) clearTimeout(this.locationSearchTimer);
  }

  get currentTicket(): TicketDraft {
    return this.tickets[this.selectedTicketIndex] || this.tickets[0];
  }

  cancel(): void {
    if (this.editEventId) {
      void this.router.navigateByUrl(`/events/${encodeURIComponent(this.editEventId)}/${this.returnTab}`);
      return;
    }
    void this.router.navigateByUrl('/events/published');
  }

  goToStep(target: number): void {
    if (target < 1 || target > 1) return;
    if (target > this.step && !this.validateStep(this.step)) return;
    this.error = '';
    this.step = target;
  }

  back(): void {
    if (this.step <= 1) return;
    this.error = '';
    this.step -= 1;
  }

  async nextOrSubmit(): Promise<void> {
    this.error = '';
    if (!this.validateStep(1)) return;
    await this.submit();
  }

  private emptyTicket(): TicketDraft {
    return {
      name: 'General Admission',
      description: '',
      pricingMethod: 'fixed',
      price: 0,
      quantityMode: 'unlimited',
      quantityTotal: null,
      maxQtyPerOrder: 5,
      status: 'available',
      descriptionFooter:
        'This is your event ticket. Ticket holders must present their ticket for entry.',
      visibility: 'show'
    };
  }

  private buildTimeOptions(): TimeOption[] {
    const options: TimeOption[] = [];
    for (let hour = 0; hour < 24; hour += 1) {
      for (let minute = 0; minute < 60; minute += 10) {
        const value = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        const displayHour = hour % 12 === 0 ? 12 : hour % 12;
        const suffix = hour < 12 ? 'AM' : 'PM';
        const label = `${displayHour}:${String(minute).padStart(2, '0')} ${suffix}`;
        options.push({ value, label });
      }
    }
    return options;
  }

  private formatDateDisplay(value: string): string {
    const trimmed = String(value || '').trim();
    if (!trimmed) return '';
    const parts = trimmed.split('-');
    if (parts.length !== 3) return trimmed;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }

  private formatDateLongDisplay(value: string): string {
    const trimmed = String(value || '').trim();
    if (!trimmed) return '';
    const parts = trimmed.split('-');
    if (parts.length !== 3) return trimmed;
    const year = Number(parts[0]);
    const monthIndex = Number(parts[1]) - 1;
    const day = Number(parts[2]);
    if (!Number.isInteger(year) || !Number.isInteger(monthIndex) || !Number.isInteger(day)) return trimmed;
    const date = new Date(Date.UTC(year, monthIndex, day));
    const label = new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC'
    }).format(date);
    return label;
  }

  private formatTimeDisplay(value: string): string {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const parts = raw.split(':');
    const hour = Number(parts[0]);
    const minute = Number(parts[1] || 0);
    if (Number.isNaN(hour) || Number.isNaN(minute)) return raw;
    const displayHour = hour % 12 === 0 ? 12 : hour % 12;
    const suffix = hour < 12 ? 'AM' : 'PM';
    return `${displayHour}:${String(minute).padStart(2, '0')} ${suffix}`;
  }

  addTicket(): void {
    this.tickets.push(this.emptyTicket());
    this.selectedTicketIndex = this.tickets.length - 1;
  }

  removeSelectedTicket(): void {
    if (this.tickets.length <= 1) return;
    this.tickets.splice(this.selectedTicketIndex, 1);
    this.selectedTicketIndex = Math.max(0, this.selectedTicketIndex - 1);
  }

  async onSlideImageSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    await this.addSlideImageFile(file);
  }

  async onSlideVideoSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    await this.addSlideVideoFile(file);
  }

  onSlideImageDragOver(event: DragEvent): void {
    event.preventDefault();
    this.slideImageDragOver = true;
  }

  onSlideImageDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.slideImageDragOver = false;
  }

  async onSlideImageDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    this.slideImageDragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    await this.addSlideImageFile(file);
  }

  private async addSlideImageFile(file: File): Promise<void> {
    const validationError = await this.validateEventImageFile(file);
    if (validationError) {
      this.error = validationError;
      return;
    }
    this.uploadingSlideMedia = true;
    this.error = '';
    try {
      const fileId = await this.uploadMediaFile(file, 'event-slide-image');
      this.eventSlides.push({
        id: this.createLocalId(),
        type: 'image',
        fileId,
        assetUrl: this.buildAssetUrl(fileId),
        cropX: 0,
        cropY: 0,
        cropZoom: 1,
        cropBoxX: 0,
        cropBoxY: 25,
        cropBoxWidth: 100,
        cropBoxHeight: 50
      });
      this.uploadedSlideMediaFileIds.add(fileId);
      this.selectedImagePreviewId = this.imageSlides[this.imageSlides.length - 1]?.id || this.selectedImagePreviewId;
      this.syncCoverImageState();
      if (this.selectedImagePreviewId) {
        this.openImageCropper(this.selectedImagePreviewId);
      }
    } catch (error: unknown) {
      this.error = error instanceof Error ? error.message : 'Failed to upload slide image.';
    } finally {
      this.uploadingSlideMedia = false;
    }
  }

  private async validateEventImageFile(file: File): Promise<string | null> {
    const mimeType = String(file.type || '').toLowerCase();
    if (!this.EVENT_IMAGE_MIME_TYPES.includes(mimeType)) {
      return 'Only JPEG and PNG images are supported.';
    }
    if (file.size > this.EVENT_IMAGE_MAX_BYTES) {
      return 'Image size must be 10MB or less.';
    }
    const dims = await this.readImageDimensions(file);
    if (!dims) {
      return 'Unable to read image dimensions. Please try another image.';
    }
    if (dims.width < this.EVENT_IMAGE_MIN_DIMENSION || dims.height < this.EVENT_IMAGE_MIN_DIMENSION) {
      return `Image must be at least ${this.EVENT_IMAGE_MIN_DIMENSION} x ${this.EVENT_IMAGE_MIN_DIMENSION}px.`;
    }
    return null;
  }

  private readImageDimensions(file: File): Promise<{ width: number; height: number } | null> {
    return new Promise((resolve) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        const width = Number(img.naturalWidth || 0);
        const height = Number(img.naturalHeight || 0);
        URL.revokeObjectURL(objectUrl);
        resolve({ width, height });
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(null);
      };
      img.src = objectUrl;
    });
  }

  private async addSlideVideoFile(file: File): Promise<void> {
    this.uploadingSlideMedia = true;
    this.error = '';
    try {
      const fileId = await this.uploadMediaFile(file, 'event-slide-video');
      this.eventSlides.push({
        id: this.createLocalId(),
        type: 'video',
        fileId,
        assetUrl: this.buildAssetUrl(fileId)
      });
      this.uploadedSlideMediaFileIds.add(fileId);
    } catch (error: unknown) {
      this.error = error instanceof Error ? error.message : 'Failed to upload slide video.';
    } finally {
      this.uploadingSlideMedia = false;
    }
  }

  removeEventSlide(id: string): void {
    this.eventSlides = this.eventSlides.filter((slide) => slide.id !== id);
    if (this.selectedImagePreviewId === id) {
      this.selectedImagePreviewId = this.imageSlides[0]?.id || '';
    }
    this.syncCoverImageState();
  }

  removeCurrentPreviewImage(): void {
    const slide = this.previewImageSlide;
    if (!slide) return;
    this.removeEventSlide(slide.id);
  }

  openMediaEditor(event?: Event): void {
    event?.stopPropagation();
    if (this.mediaEditorOpen) return;
    this.mediaEditorOpen = true;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.highlightsModalOpen) return;
    const panel = this.mediaEditorPanel?.nativeElement;
    const overviewPanel = this.overviewPanel?.nativeElement;
    const dateLocationPanel = this.dateLocationPanel?.nativeElement;
    const goodToKnowPanel = this.goodToKnowPanel?.nativeElement;
    const target = event.target as Node | null;
    if (!target) return;

    if (!this.cropDialogOpen && this.mediaEditorOpen && panel && !panel.contains(target)) {
      this.mediaEditorOpen = false;
    }

    if (!this.overviewCollapsed && this.isOverviewComplete && overviewPanel && !overviewPanel.contains(target)) {
      this.overviewCollapsed = true;
    }

    if (!this.dateLocationCollapsed && this.isDateLocationComplete && dateLocationPanel && !dateLocationPanel.contains(target)) {
      this.dateLocationCollapsed = true;
    }

    if (!this.goodToKnowCollapsed && goodToKnowPanel && !goodToKnowPanel.contains(target)) {
      this.goodToKnowCollapsed = true;
    }
  }

  expandOverview(): void {
    if (!this.overviewCollapsed) return;
    this.overviewCollapsed = false;
  }

  openHighlightsModal(event?: Event): void {
    event?.stopPropagation();
    this.goodToKnowCollapsed = false;
    this.highlightsModalOpen = true;
  }

  closeHighlightsModal(): void {
    this.highlightsModalOpen = false;
  }

  applyHighlightsModal(): void {
    this.highlightsModalOpen = false;
    this.goodToKnowCollapsed = false;
  }

  addFaqItem(event?: Event): void {
    event?.stopPropagation();
    this.goodToKnowCollapsed = false;
    this.faqItems.push({
      id: this.createLocalId(),
      question: '',
      answer: ''
    });
  }

  removeFaqItem(id: string, event?: Event): void {
    event?.stopPropagation();
    this.goodToKnowCollapsed = false;
    this.faqItems = this.faqItems.filter((faq) => faq.id !== id);
  }

  onGoodToKnowFieldChange(): void {
    this.goodToKnowCollapsed = false;
  }

  expandGoodToKnow(): void {
    if (!this.goodToKnowCollapsed) return;
    this.goodToKnowCollapsed = false;
  }

  openLineupModal(): void {
    this.lineupDraftSpeakers = this.lineupSpeakers.map((speaker) => ({ ...speaker }));
    if (!this.lineupDraftSpeakers.length) {
      this.lineupDraftSpeakers.push(this.createLineupSpeaker());
    }
    this.lineupModalOpen = true;
  }

  cancelLineupModal(): void {
    this.lineupModalOpen = false;
    this.lineupPendingImageTargetId = '';
    this.cancelLineupImageCropper();
  }

  saveLineupModal(): void {
    this.lineupSpeakers = this.lineupDraftSpeakers.map((speaker) => ({ ...speaker }));
    this.lineupModalOpen = false;
    this.lineupPendingImageTargetId = '';
  }

  addLineupDraftSpeaker(): void {
    this.lineupDraftSpeakers.push(this.createLineupSpeaker());
  }

  removeLineupDraftSpeaker(id: string): void {
    this.lineupDraftSpeakers = this.lineupDraftSpeakers.filter((speaker) => speaker.id !== id);
    if (!this.lineupDraftSpeakers.length) {
      this.lineupDraftSpeakers.push(this.createLineupSpeaker());
    }
  }

  openLineupImagePicker(speakerId: string, input: HTMLInputElement): void {
    this.lineupPendingImageTargetId = speakerId;
    input.value = '';
    input.click();
  }

  async onLineupImageSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    const speakerId = this.lineupPendingImageTargetId;
    if (!input || !file || !speakerId) {
      if (input) input.value = '';
      return;
    }
    this.error = '';
    try {
      const fileId = await this.uploadMediaFile(file, 'lineup');
      this.uploadedSlideMediaFileIds.add(fileId);
      const imageUrl = this.buildAssetUrl(fileId);
      this.lineupDraftSpeakers = this.lineupDraftSpeakers.map((speaker) =>
        speaker.id === speakerId
          ? {
              ...speaker,
              fileId,
              assetUrl: imageUrl,
              croppedPreviewUrl: '',
              cropBoxX: 0,
              cropBoxY: 0,
              cropBoxWidth: 100,
              cropBoxHeight: 100
            }
          : speaker
      );
      this.openLineupImageCropper(speakerId);
    } catch (error: unknown) {
      this.error = error instanceof Error ? error.message : 'Failed to upload lineup image.';
    } finally {
      input.value = '';
      this.lineupPendingImageTargetId = '';
    }
  }

  openLineupImageCropper(speakerId: string): void {
    const speaker = this.lineupDraftSpeakers.find((item) => item.id === speakerId);
    if (!speaker || !speaker.assetUrl) return;
    this.lineupCropDialogTargetSpeakerId = speakerId;
    this.lineupCropDialogImageUrl = speaker.assetUrl;
    this.lineupCropBoxX = Number(speaker.cropBoxX || 0);
    this.lineupCropBoxY = Number(speaker.cropBoxY || 0);
    this.lineupCropBoxWidth = Number(speaker.cropBoxWidth || 100);
    this.lineupCropBoxHeight = Number(speaker.cropBoxHeight || 100);
    this.lineupCropDialogOpen = true;
  }

  cancelLineupImageCropper(): void {
    this.lineupCropDialogOpen = false;
    this.lineupCropDragActive = false;
    this.lineupCropResizeMode = '';
    this.lineupCropResizeHandle = '';
    this.lineupCropDialogTargetSpeakerId = '';
    this.lineupCropDialogImageUrl = '';
  }

  async applyLineupImageCropper(): Promise<void> {
    if (!this.lineupCropDialogTargetSpeakerId || !this.lineupCropDialogImageUrl) return;
    const preview = await this.generateCroppedPreviewDataUrl(this.lineupCropDialogImageUrl, {
      cropBoxX: this.lineupCropBoxX,
      cropBoxY: this.lineupCropBoxY,
      cropBoxWidth: this.lineupCropBoxWidth,
      cropBoxHeight: this.lineupCropBoxHeight,
      frameAspectRatio: 1,
      outputWidth: 640,
      outputHeight: 640
    });
    this.lineupDraftSpeakers = this.lineupDraftSpeakers.map((speaker) =>
      speaker.id === this.lineupCropDialogTargetSpeakerId
        ? {
            ...speaker,
            croppedPreviewUrl: preview || speaker.croppedPreviewUrl,
            cropBoxX: this.lineupCropBoxX,
            cropBoxY: this.lineupCropBoxY,
            cropBoxWidth: this.lineupCropBoxWidth,
            cropBoxHeight: this.lineupCropBoxHeight
          }
        : speaker
    );
    this.cancelLineupImageCropper();
  }

  displayLineupImageUrl(speaker: LineupSpeaker): string {
    return speaker.croppedPreviewUrl || speaker.assetUrl || '';
  }

  expandDateLocation(): void {
    if (!this.dateLocationCollapsed) return;
    this.dateLocationCollapsed = false;
  }

  onDateLocationFieldChange(): void {
    this.dateLocationCollapsed = false;
  }

  setEventTimeMode(mode: 'single' | 'recurring'): void {
    this.details.eventTimeMode = mode;
    this.onDateLocationFieldChange();
  }

  setLocationMode(mode: 'physical' | 'online'): void {
    if (this.details.locationMode === mode) {
      this.onDateLocationFieldChange();
      return;
    }
    this.details.locationMode = mode;
    this.resetLocation();
    this.onDateLocationFieldChange();
  }

  selectImagePreview(id: string): void {
    this.selectedImagePreviewId = id;
    this.syncCoverImageState();
  }

  onThumbDragStart(id: string, event: DragEvent): void {
    this.draggingThumbId = id;
    this.dragOverThumbId = '';
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', id);
    }
  }

  onThumbDragEnter(targetId: string, event: DragEvent): void {
    this.onThumbDragOverTarget(targetId, event);
  }

  onThumbDragOver(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  }

  onThumbDragOverTarget(targetId: string, event: DragEvent): void {
    this.onThumbDragOver(event);
    const sourceId = this.draggingThumbId || event.dataTransfer?.getData('text/plain') || '';
    if (!sourceId || sourceId === targetId) return;
    if (this.dragOverThumbId === targetId) return;

    this.dragOverThumbId = targetId;
    const imageIds = this.imageSlides.map((slide) => slide.id);
    const sourceIndex = imageIds.indexOf(sourceId);
    const targetIndex = imageIds.indexOf(targetId);
    if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) return;

    imageIds.splice(sourceIndex, 1);
    imageIds.splice(targetIndex, 0, sourceId);
    this.reorderEventSlidesByImageOrder(imageIds);
    this.syncCoverImageState();
  }

  onThumbDrop(targetId: string, event: DragEvent): void {
    event.preventDefault();
    const sourceId = this.draggingThumbId || event.dataTransfer?.getData('text/plain') || '';
    if (!sourceId || sourceId === targetId) {
      this.draggingThumbId = '';
      this.dragOverThumbId = '';
      return;
    }
    const images = [...this.imageSlides];
    const sourceIndex = images.findIndex((slide) => slide.id === sourceId);
    const targetIndex = images.findIndex((slide) => slide.id === targetId);
    if (sourceIndex < 0 || targetIndex < 0) {
      this.draggingThumbId = '';
      this.dragOverThumbId = '';
      return;
    }
    const [moved] = images.splice(sourceIndex, 1);
    images.splice(targetIndex, 0, moved);
    this.reorderEventSlidesByImageOrder(images.map((slide) => slide.id));
    this.syncCoverImageState();
    this.draggingThumbId = '';
    this.dragOverThumbId = '';
  }

  onThumbDragEnd(): void {
    this.draggingThumbId = '';
    this.dragOverThumbId = '';
  }

  openImageCropper(slideId: string): void {
    const slide = this.eventSlides.find((item) => item.id === slideId && item.type === 'image');
    if (!slide) return;
    this.cropDialogTargetSlideId = slideId;
    this.cropDialogImageUrl = slide.assetUrl;
    this.cropBoxWidth = Number(slide.cropBoxWidth || 100);
    this.cropBoxHeight = Number(slide.cropBoxHeight || 50);
    this.cropBoxX = Number(slide.cropBoxX || 0);
    this.cropBoxY = Number(slide.cropBoxY || 25);
    this.cropDraftX = 0;
    this.cropDraftY = 0;
    this.cropDraftZoom = 1;
    this.cropDialogOpen = true;
  }

  cancelImageCropper(): void {
    this.cropDialogOpen = false;
    this.cropDragActive = false;
    this.cropResizeMode = '';
    this.cropResizeHandle = '';
    this.cropDialogTargetSlideId = '';
    this.cropDialogImageUrl = '';
  }

  async applyImageCropper(): Promise<void> {
    if (!this.cropDialogTargetSlideId) return;
    const sourceImageUrl = this.cropDialogImageUrl;
    const selectorWidth = Math.max(20, Math.min(100, this.cropBoxWidth));
    const selectorHeight = Math.max(20, Math.min(100, this.cropBoxHeight));
    const centerX = this.cropBoxX + (selectorWidth / 2);
    const centerY = this.cropBoxY + (selectorHeight / 2);
    const nextZoom = Math.min(3, Math.max(1, 100 / selectorWidth));
    // Map selector center directly to object-position offset.
    // Moving the selector right/down should show right/down in preview.
    const nextCropX = Math.max(-50, Math.min(50, centerX - 50));
    const nextCropY = Math.max(-50, Math.min(50, centerY - 50));
    const croppedPreviewUrl = await this.generateCroppedPreviewDataUrl(sourceImageUrl);

    this.eventSlides = this.eventSlides.map((slide) =>
      slide.id === this.cropDialogTargetSlideId && slide.type === 'image'
        ? {
            ...slide,
            croppedPreviewUrl: croppedPreviewUrl || slide.croppedPreviewUrl,
            cropX: nextCropX,
            cropY: nextCropY,
            cropZoom: nextZoom,
            cropBoxX: this.cropBoxX,
            cropBoxY: this.cropBoxY,
            cropBoxWidth: this.cropBoxWidth,
            cropBoxHeight: this.cropBoxHeight
          }
        : slide
    );
    this.cancelImageCropper();
  }

  onCropSelectionPointerDown(event: PointerEvent): void {
    if (!this.cropDialogOpen || event.button !== 0) return;
    const frame = (event.currentTarget as HTMLElement | null)?.closest('.cropper-frame') as HTMLElement | null;
    if (!frame) return;
    event.preventDefault();
    event.stopPropagation();
    const rect = frame.getBoundingClientRect();
    this.cropDragActive = true;
    this.cropResizeMode = 'move';
    this.cropDragStartX = event.clientX;
    this.cropDragStartY = event.clientY;
    this.cropDragOriginBoxX = this.cropBoxX;
    this.cropDragOriginBoxY = this.cropBoxY;
    this.cropDragOriginBoxWidth = this.cropBoxWidth;
    this.cropDragOriginBoxHeight = this.cropBoxHeight;
    this.cropDragFrameWidth = Math.max(1, rect.width);
    this.cropDragFrameHeight = Math.max(1, rect.height);
  }

  onCropResizeHandlePointerDown(event: PointerEvent, handle: 'nw' | 'ne' | 'sw' | 'se'): void {
    if (!this.cropDialogOpen || event.button !== 0) return;
    const frame = (event.currentTarget as HTMLElement | null)?.closest('.cropper-frame') as HTMLElement | null;
    if (!frame) return;
    event.preventDefault();
    event.stopPropagation();
    const rect = frame.getBoundingClientRect();
    this.cropDragActive = true;
    this.cropResizeMode = 'resize';
    this.cropResizeHandle = handle;
    this.cropDragStartX = event.clientX;
    this.cropDragStartY = event.clientY;
    this.cropDragOriginBoxX = this.cropBoxX;
    this.cropDragOriginBoxY = this.cropBoxY;
    this.cropDragOriginBoxWidth = this.cropBoxWidth;
    this.cropDragOriginBoxHeight = this.cropBoxHeight;
    this.cropDragFrameWidth = Math.max(1, rect.width);
    this.cropDragFrameHeight = Math.max(1, rect.height);
  }

  onLineupCropSelectionPointerDown(event: PointerEvent): void {
    if (!this.lineupCropDialogOpen || event.button !== 0) return;
    const frame = (event.currentTarget as HTMLElement | null)?.closest('.cropper-frame') as HTMLElement | null;
    if (!frame) return;
    event.preventDefault();
    event.stopPropagation();
    const rect = frame.getBoundingClientRect();
    this.lineupCropDragActive = true;
    this.lineupCropResizeMode = 'move';
    this.lineupCropDragStartX = event.clientX;
    this.lineupCropDragStartY = event.clientY;
    this.lineupCropDragOriginBoxX = this.lineupCropBoxX;
    this.lineupCropDragOriginBoxY = this.lineupCropBoxY;
    this.lineupCropDragOriginBoxWidth = this.lineupCropBoxWidth;
    this.lineupCropDragOriginBoxHeight = this.lineupCropBoxHeight;
    this.lineupCropDragFrameWidth = Math.max(1, rect.width);
    this.lineupCropDragFrameHeight = Math.max(1, rect.height);
  }

  onLineupCropResizeHandlePointerDown(event: PointerEvent, handle: 'nw' | 'ne' | 'sw' | 'se'): void {
    if (!this.lineupCropDialogOpen || event.button !== 0) return;
    const frame = (event.currentTarget as HTMLElement | null)?.closest('.cropper-frame') as HTMLElement | null;
    if (!frame) return;
    event.preventDefault();
    event.stopPropagation();
    const rect = frame.getBoundingClientRect();
    this.lineupCropDragActive = true;
    this.lineupCropResizeMode = 'resize';
    this.lineupCropResizeHandle = handle;
    this.lineupCropDragStartX = event.clientX;
    this.lineupCropDragStartY = event.clientY;
    this.lineupCropDragOriginBoxX = this.lineupCropBoxX;
    this.lineupCropDragOriginBoxY = this.lineupCropBoxY;
    this.lineupCropDragOriginBoxWidth = this.lineupCropBoxWidth;
    this.lineupCropDragOriginBoxHeight = this.lineupCropBoxHeight;
    this.lineupCropDragFrameWidth = Math.max(1, rect.width);
    this.lineupCropDragFrameHeight = Math.max(1, rect.height);
  }

  @HostListener('document:pointermove', ['$event'])
  onDocumentPointerMove(event: PointerEvent): void {
    if (this.cropDragActive) {
      const deltaX = event.clientX - this.cropDragStartX;
      const deltaY = event.clientY - this.cropDragStartY;
      const dx = (deltaX / this.cropDragFrameWidth) * 100;
      const dy = (deltaY / this.cropDragFrameHeight) * 100;

      if (this.cropResizeMode === 'move') {
        const nextX = this.cropDragOriginBoxX + dx;
        const nextY = this.cropDragOriginBoxY + dy;
        this.cropBoxX = Math.max(0, Math.min(100 - this.cropBoxWidth, nextX));
        this.cropBoxY = Math.max(0, Math.min(100 - this.cropBoxHeight, nextY));
        return;
      }

      if (this.cropResizeMode === 'resize') {
        const minWidth = 20;
        const left = this.cropDragOriginBoxX;
        const top = this.cropDragOriginBoxY;
        const right = this.cropDragOriginBoxX + this.cropDragOriginBoxWidth;
        const bottom = this.cropDragOriginBoxY + this.cropDragOriginBoxHeight;

        if (this.cropResizeHandle === 'se') {
          const maxWidth = Math.min(100 - left, (100 - top) * 2);
          const newWidth = Math.max(minWidth, Math.min(maxWidth, this.cropDragOriginBoxWidth + dx));
          this.cropBoxWidth = newWidth;
          this.cropBoxHeight = newWidth / 2;
          this.cropBoxX = left;
          this.cropBoxY = top;
          return;
        }

        if (this.cropResizeHandle === 'sw') {
          const maxWidth = Math.min(right, (100 - top) * 2);
          const newWidth = Math.max(minWidth, Math.min(maxWidth, this.cropDragOriginBoxWidth - dx));
          this.cropBoxWidth = newWidth;
          this.cropBoxHeight = newWidth / 2;
          this.cropBoxX = right - newWidth;
          this.cropBoxY = top;
          return;
        }

        if (this.cropResizeHandle === 'ne') {
          const maxWidth = Math.min(100 - left, bottom * 2);
          const newWidth = Math.max(minWidth, Math.min(maxWidth, this.cropDragOriginBoxWidth + dx));
          this.cropBoxWidth = newWidth;
          this.cropBoxHeight = newWidth / 2;
          this.cropBoxX = left;
          this.cropBoxY = bottom - (newWidth / 2);
          return;
        }

        if (this.cropResizeHandle === 'nw') {
          const maxWidth = Math.min(right, bottom * 2);
          const newWidth = Math.max(minWidth, Math.min(maxWidth, this.cropDragOriginBoxWidth - dx));
          this.cropBoxWidth = newWidth;
          this.cropBoxHeight = newWidth / 2;
          this.cropBoxX = right - newWidth;
          this.cropBoxY = bottom - (newWidth / 2);
        }
      }
      return;
    }

    if (!this.lineupCropDragActive) return;
    const deltaX = event.clientX - this.lineupCropDragStartX;
    const deltaY = event.clientY - this.lineupCropDragStartY;
    const dx = (deltaX / this.lineupCropDragFrameWidth) * 100;
    const dy = (deltaY / this.lineupCropDragFrameHeight) * 100;

    if (this.lineupCropResizeMode === 'move') {
      const nextX = this.lineupCropDragOriginBoxX + dx;
      const nextY = this.lineupCropDragOriginBoxY + dy;
      this.lineupCropBoxX = Math.max(0, Math.min(100 - this.lineupCropBoxWidth, nextX));
      this.lineupCropBoxY = Math.max(0, Math.min(100 - this.lineupCropBoxHeight, nextY));
      return;
    }

    if (this.lineupCropResizeMode === 'resize') {
      const minSize = 20;
      const left = this.lineupCropDragOriginBoxX;
      const top = this.lineupCropDragOriginBoxY;
      const right = this.lineupCropDragOriginBoxX + this.lineupCropDragOriginBoxWidth;
      const bottom = this.lineupCropDragOriginBoxY + this.lineupCropDragOriginBoxHeight;

      if (this.lineupCropResizeHandle === 'se') {
        const maxSize = Math.min(100 - left, 100 - top);
        const newSize = Math.max(minSize, Math.min(maxSize, this.lineupCropDragOriginBoxWidth + Math.max(dx, dy)));
        this.lineupCropBoxWidth = newSize;
        this.lineupCropBoxHeight = newSize;
        this.lineupCropBoxX = left;
        this.lineupCropBoxY = top;
        return;
      }

      if (this.lineupCropResizeHandle === 'sw') {
        const maxSize = Math.min(right, 100 - top);
        const newSize = Math.max(minSize, Math.min(maxSize, this.lineupCropDragOriginBoxWidth - dx));
        this.lineupCropBoxWidth = newSize;
        this.lineupCropBoxHeight = newSize;
        this.lineupCropBoxX = right - newSize;
        this.lineupCropBoxY = top;
        return;
      }

      if (this.lineupCropResizeHandle === 'ne') {
        const maxSize = Math.min(100 - left, bottom);
        const newSize = Math.max(minSize, Math.min(maxSize, this.lineupCropDragOriginBoxWidth + dx));
        this.lineupCropBoxWidth = newSize;
        this.lineupCropBoxHeight = newSize;
        this.lineupCropBoxX = left;
        this.lineupCropBoxY = bottom - newSize;
        return;
      }

      if (this.lineupCropResizeHandle === 'nw') {
        const maxSize = Math.min(right, bottom);
        const newSize = Math.max(minSize, Math.min(maxSize, this.lineupCropDragOriginBoxWidth - dx));
        this.lineupCropBoxWidth = newSize;
        this.lineupCropBoxHeight = newSize;
        this.lineupCropBoxX = right - newSize;
        this.lineupCropBoxY = bottom - newSize;
      }
    }
  }

  @HostListener('document:pointerup')
  onDocumentPointerUp(): void {
    this.cropDragActive = false;
    this.cropResizeMode = '';
    this.cropResizeHandle = '';
    this.lineupCropDragActive = false;
    this.lineupCropResizeMode = '';
    this.lineupCropResizeHandle = '';
  }

  private async generateCroppedPreviewDataUrl(
    imageUrl: string,
    options?: {
      cropBoxX?: number;
      cropBoxY?: number;
      cropBoxWidth?: number;
      cropBoxHeight?: number;
      frameAspectRatio?: number;
      outputWidth?: number;
      outputHeight?: number;
    }
  ): Promise<string | null> {
    if (!imageUrl) return null;
    try {
      const imageEl = await this.loadImageForCrop(imageUrl);
      const naturalWidth = Math.max(1, imageEl.naturalWidth || imageEl.width);
      const naturalHeight = Math.max(1, imageEl.naturalHeight || imageEl.height);
      const imageRatio = naturalWidth / naturalHeight;
      // The event cover crop UI renders the source image inside a square frame.
      // The crop box itself is 2:1, but the letterboxed visible image must be
      // mapped against the actual frame ratio to export the same area the user sees.
      const frameRatio = Number(options?.frameAspectRatio || 1);

      // Crop dialog uses object-fit: contain, so map selector inside the visible letterboxed area.
      let visibleX = 0;
      let visibleY = 0;
      let visibleWidth = 1;
      let visibleHeight = 1;
      if (imageRatio > frameRatio) {
        // Wider than frame => full width, letterbox top/bottom.
        visibleWidth = 1;
        visibleHeight = frameRatio / imageRatio;
        visibleY = (1 - visibleHeight) / 2;
      } else {
        // Taller (or equal) => full height, letterbox left/right.
        visibleHeight = 1;
        visibleWidth = imageRatio / frameRatio;
        visibleX = (1 - visibleWidth) / 2;
      }

      const boxLeft = Number(options?.cropBoxX ?? this.cropBoxX) / 100;
      const boxTop = Number(options?.cropBoxY ?? this.cropBoxY) / 100;
      const boxWidth = Number(options?.cropBoxWidth ?? this.cropBoxWidth) / 100;
      const boxHeight = Number(options?.cropBoxHeight ?? this.cropBoxHeight) / 100;

      // Intersect selector with visible image region first.
      const selLeft = Math.max(boxLeft, visibleX);
      const selTop = Math.max(boxTop, visibleY);
      const selRight = Math.min(boxLeft + boxWidth, visibleX + visibleWidth);
      const selBottom = Math.min(boxTop + boxHeight, visibleY + visibleHeight);
      const clippedWidth = Math.max(0.0001, selRight - selLeft);
      const clippedHeight = Math.max(0.0001, selBottom - selTop);

      const sourceLeft = ((selLeft - visibleX) / visibleWidth) * naturalWidth;
      const sourceTop = ((selTop - visibleY) / visibleHeight) * naturalHeight;
      const sourceWidth = (clippedWidth / visibleWidth) * naturalWidth;
      const sourceHeight = (clippedHeight / visibleHeight) * naturalHeight;

      const sx = Math.max(0, Math.min(naturalWidth - 1, sourceLeft));
      const sy = Math.max(0, Math.min(naturalHeight - 1, sourceTop));
      const maxSw = naturalWidth - sx;
      const maxSh = naturalHeight - sy;
      const sw = Math.max(1, Math.min(maxSw, sourceWidth));
      const sh = Math.max(1, Math.min(maxSh, sourceHeight));

      const canvas = document.createElement('canvas');
      const outputWidth = Math.max(1, Number(options?.outputWidth || 2160));
      const outputHeight = Math.max(1, Number(options?.outputHeight || 1080));
      canvas.width = outputWidth;
      canvas.height = outputHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      // Prevent transparent PNG regions from turning black after JPEG export.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(imageEl, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/jpeg', 0.92);
    } catch {
      return null;
    }
  }

  private async generateCroppedPreviewDataUrlFromBox(
    imageUrl: string,
    cropBoxX: number,
    cropBoxY: number,
    cropBoxWidth: number,
    cropBoxHeight: number
  ): Promise<string | null> {
    const prevX = this.cropBoxX;
    const prevY = this.cropBoxY;
    const prevW = this.cropBoxWidth;
    const prevH = this.cropBoxHeight;
    this.cropBoxX = cropBoxX;
    this.cropBoxY = cropBoxY;
    this.cropBoxWidth = cropBoxWidth;
    this.cropBoxHeight = cropBoxHeight;
    try {
      return await this.generateCroppedPreviewDataUrl(imageUrl, {
        cropBoxX,
        cropBoxY,
        cropBoxWidth,
        cropBoxHeight,
        frameAspectRatio: 1,
        outputWidth: 2160,
        outputHeight: 1080
      });
    } finally {
      this.cropBoxX = prevX;
      this.cropBoxY = prevY;
      this.cropBoxWidth = prevW;
      this.cropBoxHeight = prevH;
    }
  }

  private loadImageForCrop(imageUrl: string): Promise<HTMLImageElement> {
    const loadFromSource = (src: string): Promise<HTMLImageElement> =>
      new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Failed to load image for crop preview'));
        image.src = src;
      });

    const tryBlobFirst = async (): Promise<HTMLImageElement> => {
      const headers: Record<string, string> = {};
      if (this.directus.token) headers['Authorization'] = `Bearer ${this.directus.token}`;
      const response = await fetch(imageUrl, { headers, credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch image blob for cropping');
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      try {
        return await loadFromSource(objectUrl);
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    };

    return tryBlobFirst().catch(() => loadFromSource(imageUrl));
  }

  addTextModule(): void {
    this.overviewCollapsed = false;
    this.overviewModules.push(this.createTextModule(''));
  }

  addImageModule(): void {
    this.overviewCollapsed = false;
    if (this.overviewImageCount >= this.OVERVIEW_MAX_IMAGES) {
      this.error = `Overview can contain max ${this.OVERVIEW_MAX_IMAGES} images.`;
      return;
    }
    this.overviewModules.push({
      id: this.createLocalId(),
      type: 'image',
      textHtml: '',
      fileId: '',
      assetUrl: '',
      videoUrl: ''
    });
  }

  addVideoModule(): void {
    this.overviewCollapsed = false;
    if (this.overviewVideoCount >= this.OVERVIEW_MAX_VIDEOS) {
      this.error = `Overview can contain max ${this.OVERVIEW_MAX_VIDEOS} videos.`;
      return;
    }
    this.overviewModules.push({
      id: this.createLocalId(),
      type: 'video',
      textHtml: '',
      fileId: '',
      assetUrl: '',
      videoUrl: ''
    });
  }

  triggerOverviewImageUpload(moduleId: string): void {
    const input = document.getElementById(`overview-image-input-${moduleId}`) as HTMLInputElement | null;
    input?.click();
  }

  onOverviewImageModuleDragOver(moduleId: string, event: DragEvent): void {
    event.preventDefault();
    this.overviewImageDragModuleId = moduleId;
  }

  onOverviewImageModuleDragLeave(moduleId: string, event: DragEvent): void {
    event.preventDefault();
    if (this.overviewImageDragModuleId === moduleId) {
      this.overviewImageDragModuleId = '';
    }
  }

  async onOverviewImageModuleDrop(moduleId: string, event: DragEvent): Promise<void> {
    event.preventDefault();
    this.overviewImageDragModuleId = '';
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    await this.uploadOverviewImageToModule(moduleId, file);
  }

  async onOverviewImageModuleSelected(moduleId: string, event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    await this.uploadOverviewImageToModule(moduleId, file);
  }

  private async uploadOverviewImageToModule(moduleId: string, file: File): Promise<void> {
    if (this.overviewImageCount >= this.OVERVIEW_MAX_IMAGES) {
      this.error = `Overview can contain max ${this.OVERVIEW_MAX_IMAGES} images.`;
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      this.error = 'Each overview image must be 4MB or less.';
      return;
    }
    this.uploadingOverviewMedia = true;
    this.error = '';
    try {
      const fileId = await this.uploadMediaFile(file, 'overview-image');
      this.overviewModules = this.overviewModules.map((module) =>
        module.id === moduleId && module.type === 'image'
          ? { ...module, fileId, assetUrl: this.buildAssetUrl(fileId) }
          : module
      );
      this.uploadedSlideMediaFileIds.add(fileId);
      this.overviewCollapsed = false;
    } catch (error: unknown) {
      this.error = error instanceof Error ? error.message : 'Failed to upload overview image.';
    } finally {
      this.uploadingOverviewMedia = false;
    }
  }

  onOverviewVideoUrlInput(moduleId: string, value: string): void {
    this.overviewCollapsed = false;
    this.overviewModules = this.overviewModules.map((module) =>
      module.id === moduleId && module.type === 'video'
        ? { ...module, videoUrl: String(value || '').trim() }
        : module
    );
  }

  removeOverviewModule(id: string): void {
    this.overviewCollapsed = false;
    this.overviewModules = this.overviewModules.filter((module) => module.id !== id);
    if (!this.overviewModules.some((module) => module.type === 'text')) {
      this.overviewModules.unshift(this.createTextModule(''));
    }
  }

  moveOverviewModuleUp(index: number): void {
    if (index <= 0 || index >= this.overviewModules.length) return;
    this.overviewCollapsed = false;
    const next = [...this.overviewModules];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    this.overviewModules = next;
  }

  moveOverviewModuleDown(index: number): void {
    if (index < 0 || index >= this.overviewModules.length - 1) return;
    this.overviewCollapsed = false;
    const next = [...this.overviewModules];
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
    this.overviewModules = next;
  }

  onOverviewTextInput(moduleId: string, event: Event): void {
    this.overviewCollapsed = false;
    const target = event.target as HTMLDivElement;
    const html = target.innerHTML || '';
    const module = this.overviewModules.find((item) => item.id === moduleId && item.type === 'text');
    if (module && module.type === 'text') {
      module.textHtml = html;
    }
  }

  applyModuleEditorCommand(moduleId: string, command: string): void {
    const editor = document.getElementById(`module-editor-${moduleId}`) as HTMLDivElement | null;
    if (!editor) return;
    this.overviewCollapsed = false;
    editor.focus();
    document.execCommand(command);
    const module = this.overviewModules.find((item) => item.id === moduleId && item.type === 'text');
    if (module && module.type === 'text') {
      module.textHtml = editor.innerHTML || '';
    }
  }

  insertModuleLink(moduleId: string): void {
    const raw = window.prompt('Enter URL');
    if (!raw) return;
    const url = raw.trim();
    if (!url || !/^https?:\/\/[^\s]+$/i.test(url)) return;
    const editor = document.getElementById(`module-editor-${moduleId}`) as HTMLDivElement | null;
    if (!editor) return;
    this.overviewCollapsed = false;
    editor.focus();
    document.execCommand('createLink', false, url);
    const module = this.overviewModules.find((item) => item.id === moduleId && item.type === 'text');
    if (module && module.type === 'text') {
      module.textHtml = editor.innerHTML || '';
    }
  }

  trackByOverviewModuleId(_: number, module: OverviewModule): string {
    return module.id;
  }

  async searchLocation(): Promise<void> {
    const query = this.locationSearch.trim();
    if (!query) return;

    this.error = '';
    this.locationError = '';
    this.searchingLocation = true;

    try {
      const suggestions = await this.fetchLocationSuggestions(query);
      if (suggestions.length > 0) {
        await this.selectLocation(suggestions[0]);
      } else {
        this.locationSuggestions = [];
        this.locationError = 'No Google location results found. Try a more specific address in Indonesia.';
      }
    } catch {
      this.locationSuggestions = [];
      this.locationError = 'Location lookup failed. Check Google API key restrictions and try again.';
    } finally {
      this.searchingLocation = false;
    }
  }

  onLocationInput(value: string): void {
    this.locationSearch = value;
    if (this.locationSearchTimer) clearTimeout(this.locationSearchTimer);
    const query = value.trim();
    if (!query || query.length < 3) {
      this.locationSuggestions = [];
      this.locationError = '';
      return;
    }
    this.locationSearchTimer = setTimeout(async () => {
      const seq = ++this.locationRequestSeq;
      this.searchingLocation = true;
      const suggestions = await this.fetchLocationSuggestions(query);
      if (seq === this.locationRequestSeq) {
        this.locationSuggestions = suggestions;
        this.locationError = suggestions.length === 0 ? 'No Google location results found.' : '';
      }
      this.searchingLocation = false;
    }, 120);
  }

  async selectLocation(suggestion: LocationSuggestion): Promise<void> {
    let normalized = await this.resolveSuggestionToSelection(suggestion);
    normalized = await this.validateAddressWithGoogle(normalized);
    this.selectedLocation = normalized;
    this.updateMapEmbedUrl();
    this.locationSearch = '';
    this.locationSuggestions = [];
    this.locationError = '';
    this.onDateLocationFieldChange();
  }

  private async fetchLocationSuggestions(query: string): Promise<LocationSuggestion[]> {
    try {
      const googleMapsApiKey = (environment as { googleMapsApiKey?: string }).googleMapsApiKey;
      if (googleMapsApiKey) {
        const response = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': googleMapsApiKey,
            'X-Goog-FieldMask':
              'suggestions.placePrediction.placeId,suggestions.placePrediction.text.text,suggestions.placePrediction.structuredFormat.mainText.text,suggestions.placePrediction.structuredFormat.secondaryText.text'
          },
          body: JSON.stringify({
            input: query,
            includedRegionCodes: ['id'],
            languageCode: 'id'
          })
        });
        if (response.ok) {
          const payload = await response.json();
          const suggestions = Array.isArray(payload?.suggestions) ? payload.suggestions : [];
          return suggestions
            .map((row: Record<string, unknown>) => {
              const pp = row['placePrediction'] as Record<string, unknown> | undefined;
              if (!pp) return null;
              const placeId = String(pp['placeId'] || '');
              const structured = pp['structuredFormat'] as Record<string, unknown> | undefined;
              const mainTextObj = structured?.['mainText'] as Record<string, unknown> | undefined;
              const secondaryTextObj = structured?.['secondaryText'] as Record<string, unknown> | undefined;
              const venueName = String(mainTextObj?.['text'] || '');
              const secondary = String(secondaryTextObj?.['text'] || '');
              const fullTextObj = pp['text'] as Record<string, unknown> | undefined;
              const fullText = String(fullTextObj?.['text'] || '');
              const address = secondary || fullText || venueName || query;
              if (!placeId || !address) return null;
              return {
                placeId,
                venueName: venueName || address.split(',')[0] || query,
                address,
                mapQuery: address
              } as LocationSuggestion;
            })
            .filter((x: LocationSuggestion | null): x is LocationSuggestion => !!x)
            .slice(0, 5);
        }
        const autocompleteError = await response.json().catch(() => ({}));
        const acMessage = String((autocompleteError as Record<string, unknown>)?.['error'] && (autocompleteError as Record<string, unknown>)['error'] as unknown || '');

        const textSearchResponse = await fetch('https://places.googleapis.com/v1/places:searchText', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': googleMapsApiKey,
            'X-Goog-FieldMask': 'places.id,places.displayName.text,places.formattedAddress'
          },
          body: JSON.stringify({
            textQuery: query,
            regionCode: 'ID',
            languageCode: 'id'
          })
        });
        if (textSearchResponse.ok) {
          const payload = await textSearchResponse.json();
          const places = Array.isArray(payload?.places) ? payload.places : [];
          return places
            .map((p: Record<string, unknown>) => {
              const placeId = String(p['id'] || '');
              const dn = p['displayName'] as Record<string, unknown> | undefined;
              const venueName = String(dn?.['text'] || '');
              const address = String(p['formattedAddress'] || '');
              if (!placeId || !address) return null;
              return {
                placeId,
                venueName: venueName || address.split(',')[0] || query,
                address,
                mapQuery: address
              } as LocationSuggestion;
            })
            .filter((x: LocationSuggestion | null): x is LocationSuggestion => !!x)
            .slice(0, 5);
        }
        const textSearchError = await textSearchResponse.json().catch(() => ({}));
        const tsMessage = String((textSearchError as Record<string, unknown>)?.['error'] && (textSearchError as Record<string, unknown>)['error'] as unknown || '');
        this.locationError = tsMessage || acMessage || 'Google Places request failed.';
        return [];
      }

      this.locationError = 'Google Maps API key is missing from deployment.';
      return [];
    } catch {
      this.locationError = 'Network error while loading Google location results.';
      return [];
    }
  }

  private async resolveSuggestionToSelection(suggestion: LocationSuggestion): Promise<LocationSelection> {
    try {
      const googleMapsApiKey = (environment as { googleMapsApiKey?: string }).googleMapsApiKey;
      if (!googleMapsApiKey || !suggestion.placeId) {
        return {
          venueName: suggestion.venueName,
          address: suggestion.address,
          mapQuery: suggestion.mapQuery
        };
      }

      const endpoint = `https://places.googleapis.com/v1/places/${encodeURIComponent(suggestion.placeId)}`;
      const response = await fetch(endpoint, {
        headers: {
          'X-Goog-Api-Key': googleMapsApiKey,
          'X-Goog-FieldMask': 'displayName.text,formattedAddress'
        }
      });

      if (!response.ok) {
        return {
          venueName: suggestion.venueName,
          address: suggestion.address,
          mapQuery: suggestion.mapQuery
        };
      }

      const payload = await response.json();
      const venueName = String(payload?.displayName?.text || suggestion.venueName || '');
      const address = String(payload?.formattedAddress || suggestion.address || '');

      return {
        venueName: venueName || address.split(',')[0] || suggestion.venueName,
        address: address || suggestion.address,
        mapQuery: address || suggestion.mapQuery
      };
    } catch {
      return {
        venueName: suggestion.venueName,
        address: suggestion.address,
        mapQuery: suggestion.mapQuery
      };
    }
  }

  private async validateAddressWithGoogle(selection: LocationSelection): Promise<LocationSelection> {
    try {
      const googleMapsApiKey = (environment as { googleMapsApiKey?: string }).googleMapsApiKey;
      if (!googleMapsApiKey) return selection;

      const response = await fetch(
        `https://addressvalidation.googleapis.com/v1:validateAddress?key=${encodeURIComponent(googleMapsApiKey)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            address: {
              regionCode: 'ID',
              addressLines: [selection.address]
            }
          })
        }
      );

      if (!response.ok) return selection;
      const payload = await response.json();
      const formatted = String(
        payload?.result?.address?.formattedAddress ||
        payload?.result?.geocode?.formattedAddress ||
        selection.address
      ).trim();

      if (!formatted) return selection;
      return {
        venueName: selection.venueName,
        address: formatted,
        mapQuery: formatted
      };
    } catch {
      return selection;
    }
  }

  private async initGooglePlacesWidget(): Promise<void> {
    if (this.details.locationMode !== 'physical' || this.selectedLocation) return;
    const host = this.googlePlacesHostEl?.nativeElement;
    if (!host || this.googlePlacesWidgetAttached) return;

    const key = String((environment as { googleMapsApiKey?: string }).googleMapsApiKey || '').trim();
    if (!key || key.includes('__GOOGLE_MAPS_API_KEY__')) {
      this.locationError = 'Google Maps key is not configured in this deployment.';
      return;
    }

    this.searchingLocation = true;
    this.locationError = '';
    try {
      await this.loadGoogleMapsScript(key);
      const googleAny = (window as unknown as { google?: any }).google;
      if (!googleAny?.maps?.places?.PlaceAutocompleteElement) {
        this.locationError = 'Google Places widget is unavailable.';
        return;
      }

      const placeAutocomplete = new googleAny.maps.places.PlaceAutocompleteElement();
      placeAutocomplete.includedRegionCodes = ['id'];
      placeAutocomplete.placeholder = 'Search venue or address in Indonesia';
      placeAutocomplete.style.setProperty('width', '100%');
      placeAutocomplete.style.setProperty('color-scheme', 'light');
      placeAutocomplete.style.setProperty('--gmpx-color-surface', '#ffffff');
      placeAutocomplete.style.setProperty('--gmpx-color-on-surface', '#0f172a');
      placeAutocomplete.style.setProperty('--gmpx-color-outline', '#bfdbfe');
      placeAutocomplete.style.setProperty('--gmpx-color-on-surface-variant', '#334155');
      placeAutocomplete.style.setProperty('--gmpx-color-primary', '#2563eb');
      host.style.setProperty('color-scheme', 'light');
      host.style.setProperty('background', '#ffffff');

      placeAutocomplete.addEventListener('gmp-select', async (event: any) => {
        try {
          const placePrediction = event?.placePrediction || event?.detail?.placePrediction;
          if (!placePrediction) return;
          const place = placePrediction.toPlace();
          await place.fetchFields({ fields: ['displayName', 'formattedAddress'] });
          const displayName = String(place.displayName || '');
          const formattedAddress = String(place.formattedAddress || '');
          if (!formattedAddress) return;

          let normalized: LocationSelection = {
            venueName: displayName || formattedAddress.split(',')[0] || 'Venue',
            address: formattedAddress,
            mapQuery: formattedAddress
          };
          normalized = await this.validateAddressWithGoogle(normalized);
          this.selectedLocation = normalized;
          this.updateMapEmbedUrl();
          this.locationError = '';
        } catch {
          this.locationError = 'Failed to resolve selected location.';
        }
      });

      host.innerHTML = '';
      host.appendChild(placeAutocomplete);
      this.googlePlacesWidgetAttached = true;
    } catch {
      this.locationError = 'Failed to initialize Google Places.';
    } finally {
      this.searchingLocation = false;
    }
  }

  private async loadGoogleMapsScript(key: string): Promise<void> {
    const googleAny = (window as unknown as { google?: any }).google;
    if (googleAny?.maps?.places?.PlaceAutocompleteElement) return;
    if (this.googlePlacesScriptPromise) return this.googlePlacesScriptPromise;

    this.googlePlacesScriptPromise = new Promise<void>((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>('script[data-google-maps-loader="1"]');
      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('Google Maps script failed')), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places&v=weekly`;
      script.async = true;
      script.defer = true;
      script.dataset['googleMapsLoader'] = '1';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Google Maps script failed'));
      document.head.appendChild(script);
    });

    return this.googlePlacesScriptPromise;
  }

  resetLocation(): void {
    this.selectedLocation = null;
    this.locationSearch = '';
    this.locationSuggestions = [];
    this.locationError = '';
    this.details.location = '';
    this.details.locationAddress = '';
    this.googleMapEmbedUrlSafe = this.sanitizer.bypassSecurityTrustResourceUrl('about:blank');
    this.googlePlacesWidgetAttached = false;
    void this.initGooglePlacesWidget();
    this.onDateLocationFieldChange();
  }

  syncMapFromAddress(): void {
    if (!this.selectedLocation) return;
    const address = this.selectedLocation.address.trim();
    if (!address) return;
    this.selectedLocation.mapQuery = address;
    this.updateMapEmbedUrl();
  }

  private updateMapEmbedUrl(): void {
    const query = (this.selectedLocation?.mapQuery || this.selectedLocation?.address || '').trim();
    if (!query) {
      this.googleMapEmbedUrlSafe = this.sanitizer.bypassSecurityTrustResourceUrl('about:blank');
      return;
    }
    const url = `https://maps.google.com/maps?q=${encodeURIComponent(query)}&z=15&output=embed`;
    this.googleMapEmbedUrlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  private createTextModule(textHtml: string): OverviewModule {
    return {
      id: this.createLocalId(),
      type: 'text',
      textHtml,
      fileId: '',
      assetUrl: '',
      videoUrl: ''
    };
  }

  private createLineupSpeaker(): LineupSpeaker {
    return {
      id: this.createLocalId(),
      fileId: '',
      assetUrl: '',
      croppedPreviewUrl: '',
      cropBoxX: 0,
      cropBoxY: 0,
      cropBoxWidth: 100,
      cropBoxHeight: 100,
      name: '',
      tagline: '',
      description: ''
    };
  }

  private createLocalId(): string {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }

  private buildAssetUrl(fileId: string): string {
    return this.directus.buildAssetUrl(fileId);
  }

  private stripHtml(value: string): string {
    return value
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<[^>]+>/g, '')
      .replace(/\u00a0/g, ' ')
      .trim();
  }


  private buildOverviewStoragePayload(): string {
    const payload = {
      version: 1,
      modules: this.overviewModules.map((module) => ({
        type: module.type,
        textHtml: module.type === 'text' ? module.textHtml : '',
        fileId: module.type === 'text' ? '' : module.fileId,
        videoUrl: module.type === 'video' ? module.videoUrl : ''
      })),
      goodToKnow: {
        highlights: {
          doorTimeValue: this.highlightDoorTimeValue,
          doorTimeUnit: this.highlightDoorTimeUnit,
          dressCode: this.highlightDressCode,
          parking: this.highlightParking
        },
        faqs: this.faqItems.map((faq) => ({
          question: faq.question.trim(),
          answer: faq.answer.trim()
        }))
      },
      lineup: this.lineupSpeakers.map((speaker) => ({
        fileId: speaker.fileId,
        name: speaker.name.trim(),
        tagline: speaker.tagline.trim(),
        description: speaker.description.trim(),
        cropBoxX: Number(speaker.cropBoxX || 0),
        cropBoxY: Number(speaker.cropBoxY || 0),
        cropBoxWidth: Number(speaker.cropBoxWidth || 100),
        cropBoxHeight: Number(speaker.cropBoxHeight || 100)
      })),
      slides: this.eventSlides.map((slide) => ({
        type: slide.type,
        fileId: slide.fileId,
        cropX: slide.type === 'image' ? Number(slide.cropX || 0) : 0,
        cropY: slide.type === 'image' ? Number(slide.cropY || 0) : 0,
        cropZoom: slide.type === 'image' ? Number(slide.cropZoom || 1) : 1,
        cropBoxX: slide.type === 'image' ? Number(slide.cropBoxX || 0) : 0,
        cropBoxY: slide.type === 'image' ? Number(slide.cropBoxY || 25) : 25,
        cropBoxWidth: slide.type === 'image' ? Number(slide.cropBoxWidth || 100) : 100,
        cropBoxHeight: slide.type === 'image' ? Number(slide.cropBoxHeight || 50) : 50
      }))
    };
    return JSON.stringify(payload);
  }

  private parseOverviewStoragePayload(raw: unknown): boolean {
    if (typeof raw !== 'string' || !raw.trim()) return false;
    try {
      const parsed = JSON.parse(raw) as {
        version?: unknown;
        modules?: Array<{ type?: unknown; textHtml?: unknown; fileId?: unknown; videoUrl?: unknown }>;
        goodToKnow?: {
          highlights?: {
            doorTimeValue?: unknown;
            doorTimeUnit?: unknown;
            dressCode?: unknown;
            parking?: unknown;
          };
          faqs?: Array<{ question?: unknown; answer?: unknown }>;
        };
        lineup?: Array<{
          fileId?: unknown;
          name?: unknown;
          tagline?: unknown;
          description?: unknown;
          cropBoxX?: unknown;
          cropBoxY?: unknown;
          cropBoxWidth?: unknown;
          cropBoxHeight?: unknown;
        }>;
        slides?: Array<{
          type?: unknown;
          fileId?: unknown;
          cropX?: unknown;
          cropY?: unknown;
          cropZoom?: unknown;
          cropBoxX?: unknown;
          cropBoxY?: unknown;
          cropBoxWidth?: unknown;
          cropBoxHeight?: unknown;
        }>;
      };
      if (!Array.isArray(parsed.modules)) return false;
      const modules: OverviewModule[] = parsed.modules
        .map((module): OverviewModule | null => {
          const type = module?.type === 'image' || module?.type === 'video' || module?.type === 'text'
            ? module.type
            : null;
          if (!type) return null;
          if (type === 'text') {
            return this.createTextModule(typeof module.textHtml === 'string' ? module.textHtml : '');
          }
          const fileId = typeof module.fileId === 'string' ? module.fileId.trim() : '';
          const videoUrl = typeof module.videoUrl === 'string' ? module.videoUrl.trim() : '';
          if (type === 'video' && !fileId && !videoUrl) {
            return {
              id: this.createLocalId(),
              type,
              textHtml: '',
              fileId: '',
              assetUrl: '',
              videoUrl: ''
            };
          }
          if (type === 'image' && !fileId) {
            return {
              id: this.createLocalId(),
              type,
              textHtml: '',
              fileId: '',
              assetUrl: '',
              videoUrl: ''
            };
          }
          return {
            id: this.createLocalId(),
            type,
            textHtml: '',
            fileId,
            assetUrl: fileId ? this.buildAssetUrl(fileId) : '',
            videoUrl
          };
        })
        .filter((module): module is OverviewModule => !!module);
      this.overviewModules = modules.length ? modules : [this.createTextModule('')];
      const highlights = parsed.goodToKnow?.highlights;
      this.highlightDoorTimeValue = typeof highlights?.doorTimeValue === 'number' ? highlights.doorTimeValue : null;
      this.highlightDoorTimeUnit = highlights?.doorTimeUnit === 'hours' ? 'hours' : 'minutes';
      this.highlightDressCode = highlights?.dressCode === 'jci_polo'
        || highlights?.dressCode === 'smart_casual'
        || highlights?.dressCode === 'business_attire'
        || highlights?.dressCode === 'black_tie'
        ? highlights.dressCode
        : '';
      this.highlightParking = highlights?.parking === 'free'
        || highlights?.parking === 'paid'
        || highlights?.parking === 'none'
        ? highlights.parking
        : '';
      this.faqItems = Array.isArray(parsed.goodToKnow?.faqs)
        ? parsed.goodToKnow!.faqs!
            .map((faq) => ({
              id: this.createLocalId(),
              question: typeof faq.question === 'string' ? faq.question : '',
              answer: typeof faq.answer === 'string' ? faq.answer : ''
            }))
        : [];
      this.lineupSpeakers = Array.isArray(parsed.lineup)
        ? parsed.lineup
            .map((speaker): LineupSpeaker | null => {
              const fileId = typeof speaker?.fileId === 'string' ? speaker.fileId.trim() : '';
              if (!fileId) return null;
              return {
                id: this.createLocalId(),
                fileId,
                assetUrl: this.buildAssetUrl(fileId),
                croppedPreviewUrl: '',
                cropBoxX: Number(speaker?.cropBoxX ?? 0),
                cropBoxY: Number(speaker?.cropBoxY ?? 0),
                cropBoxWidth: Number(speaker?.cropBoxWidth ?? 100),
                cropBoxHeight: Number(speaker?.cropBoxHeight ?? 100),
                name: typeof speaker?.name === 'string' ? speaker.name : '',
                tagline: typeof speaker?.tagline === 'string' ? speaker.tagline : '',
                description: typeof speaker?.description === 'string' ? speaker.description : ''
              };
            })
            .filter((speaker): speaker is LineupSpeaker => !!speaker)
        : [];
      this.eventSlides = Array.isArray(parsed.slides)
        ? parsed.slides
            .map((slide): EventSlide | null => {
              const type = slide?.type === 'image' || slide?.type === 'video' ? slide.type : null;
              const fileId = typeof slide?.fileId === 'string' ? slide.fileId.trim() : '';
              if (!type || !fileId) return null;
              const cropZoom = type === 'image' ? Number(slide?.cropZoom || 1) : 1;
              const cropX = type === 'image' ? Number(slide?.cropX || 0) : 0;
              const cropY = type === 'image' ? Number(slide?.cropY || 0) : 0;
              const hasBox =
                type === 'image'
                && slide?.cropBoxWidth !== undefined
                && slide?.cropBoxHeight !== undefined
                && slide?.cropBoxX !== undefined
                && slide?.cropBoxY !== undefined;
              const derivedWidth = Math.max(20, Math.min(100, 100 / Math.max(1, cropZoom)));
              const derivedHeight = derivedWidth / 2;
              const derivedX = Math.max(0, Math.min(100 - derivedWidth, (50 + cropX) - (derivedWidth / 2)));
              const derivedY = Math.max(0, Math.min(100 - derivedHeight, (50 + cropY) - (derivedHeight / 2)));
              return {
                id: this.createLocalId(),
                type,
                fileId,
                assetUrl: this.buildAssetUrl(fileId),
                cropX,
                cropY,
                cropZoom,
                cropBoxX: type === 'image'
                  ? (hasBox ? Number(slide?.cropBoxX || 0) : derivedX)
                  : 0,
                cropBoxY: type === 'image'
                  ? (hasBox ? Number(slide?.cropBoxY || 25) : derivedY)
                  : 25,
                cropBoxWidth: type === 'image'
                  ? (hasBox ? Number(slide?.cropBoxWidth || 100) : derivedWidth)
                  : 100,
                cropBoxHeight: type === 'image'
                  ? (hasBox ? Number(slide?.cropBoxHeight || 50) : derivedHeight)
                  : 50
              };
            })
            .filter((slide): slide is EventSlide => !!slide)
        : [];
      this.syncCoverImageState();
      void this.refreshImageSlidePreviews();
      void this.refreshLineupSpeakerPreviews();
      return true;
    } catch {
      return false;
    }
  }

  private async refreshImageSlidePreviews(): Promise<void> {
    const slides = [...this.eventSlides];
    const updated = await Promise.all(slides.map(async (slide) => {
      if (slide.type !== 'image' || !slide.assetUrl) return slide;
      const preview = await this.generateCroppedPreviewDataUrlFromBox(
        slide.assetUrl,
        Number(slide.cropBoxX || 0),
        Number(slide.cropBoxY || 25),
        Number(slide.cropBoxWidth || 100),
        Number(slide.cropBoxHeight || 50)
      );
      return preview ? { ...slide, croppedPreviewUrl: preview } : slide;
    }));
    this.eventSlides = updated;
    this.syncCoverImageState();
  }

  private async refreshLineupSpeakerPreviews(): Promise<void> {
    const speakers = [...this.lineupSpeakers];
    const updated = await Promise.all(speakers.map(async (speaker) => {
      if (!speaker.assetUrl) return speaker;
      const preview = await this.generateCroppedPreviewDataUrl(speaker.assetUrl, {
        cropBoxX: Number(speaker.cropBoxX || 0),
        cropBoxY: Number(speaker.cropBoxY || 0),
        cropBoxWidth: Number(speaker.cropBoxWidth || 100),
        cropBoxHeight: Number(speaker.cropBoxHeight || 100),
        frameAspectRatio: 1,
        outputWidth: 640,
        outputHeight: 640
      });
      return preview ? { ...speaker, croppedPreviewUrl: preview } : speaker;
    }));
    this.lineupSpeakers = updated;
  }

  private extractOverviewStructuredRaw(row: Record<string, unknown>): string {
    const structured = row['event_details'];
    if (structured && typeof structured === 'object') {
      try {
        return JSON.stringify(structured);
      } catch {
        // Fallback to legacy description
      }
    }
    return typeof row['description'] === 'string' ? row['description'] : '';
  }

  private syncCoverImageState(fallbackImageId = ''): void {
    const coverId = this.coverImageFileId || fallbackImageId;
    this.details.imageId = coverId;
    this.imagePreviewUrl = coverId
      ? this.directus.buildAssetUrl(coverId, { width: 2160, height: 1080, fit: 'cover' })
      : '';
  }

  private reorderEventSlidesByImageOrder(imageIdsInOrder: string[]): void {
    const imageById = new Map(this.imageSlides.map((slide) => [slide.id, slide]));
    const orderedImages = imageIdsInOrder
      .map((id) => imageById.get(id))
      .filter((slide): slide is EventSlide => !!slide);
    let cursor = 0;
    this.eventSlides = this.eventSlides.map((slide) => {
      if (slide.type !== 'image') return slide;
      const next = orderedImages[cursor];
      cursor += 1;
      return next || slide;
    });
  }

  private async uploadMediaFile(file: File, prefix: string): Promise<string> {
    const uploaded = await this.directus.uploadFile(file, {
      title: this.details.title?.trim() || file.name,
      description: `Uploaded from ${prefix}`
    });
    const fileId = String(uploaded['id'] || '');
    if (!fileId) throw new Error('File upload failed: missing file id.');
    return fileId;
  }

  private revokePreviewUrl(): void {
    if (this.imagePreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.imagePreviewUrl);
    }
  }

  async loadCategories(): Promise<void> {
    const rows = await this.safeList('eventcategories', { fields: 'id,name', sort: 'name', limit: 500 });
    this.categories = rows.map((row) => ({
      id: String(row['id'] ?? ''),
      name: typeof row['name'] === 'string' && row['name'].trim() ? row['name'] : `Category ${String(row['id'] ?? '')}`
    }));
  }

  async loadMembers(): Promise<void> {
    const rows = await this.safeList('profiles', {
      fields: 'id,first_name,middle_name,last_name,phone',
      'filter[subscribed][_eq]': true,
      sort: 'first_name,last_name',
      limit: 500
    });
    this.members = rows.map((row) => ({
      id: String(row['id'] ?? ''),
      name: [row['first_name'], row['middle_name'], row['last_name']]
        .filter((p) => typeof p === 'string' && (p as string).trim())
        .join(' '),
      phone: typeof row['phone'] === 'string' ? row['phone'] : ''
    }));
  }

  onProjectDirectorChange(): void {
    const member = this.members.find((m) => m.id === this.details.projectDirectorId);
    this.details.projectDirectorPhone = member?.phone || '';
  }

  async loadFeatures(): Promise<void> {
    const rows = await this.safeList('event_features', { fields: 'id,key,name,description,active', sort: 'name', limit: 500 });
    this.features = rows
      .filter((row) => row['active'] !== false)
      .map((row) => ({
        id: String(row['id'] ?? ''),
        key: typeof row['key'] === 'string' ? row['key'] : '',
        name: typeof row['name'] === 'string' && row['name'].trim() ? row['name'] : 'Feature',
        description: typeof row['description'] === 'string' ? row['description'] : '',
        selected: false
      }));
  }

  private async safeList(
    collection: string,
    query: Record<string, string | number | boolean | undefined>
  ): Promise<Record<string, unknown>[]> {
    try {
      return await this.directus.list(collection, query);
    } catch {
      return [];
    }
  }

  private validateStep(step: number): boolean {
    if (step === 1) {
      if (!this.details.title.trim()) return this.fail('Event name is required.');
      if (!this.details.projectDirectorId) return this.fail('Project Director is required.');
      if (!this.overviewModules.some((module) => module.type === 'text')) {
        return this.fail('Overview must include at least one text module.');
      }
      const overLimitTextBlock = this.overviewModules.find(
        (module) => module.type === 'text' && this.overviewTextModuleCharCount(module) > this.OVERVIEW_TEXT_MAX_CHARS
      );
      if (overLimitTextBlock) {
        return this.fail(`Each text block in Overview is limited to ${this.OVERVIEW_TEXT_MAX_CHARS} characters.`);
      }
      if (this.overviewImageCount > this.OVERVIEW_MAX_IMAGES) {
        return this.fail(`Overview images exceed the ${this.OVERVIEW_MAX_IMAGES} image limit.`);
      }
      if (this.overviewVideoCount > this.OVERVIEW_MAX_VIDEOS) {
        return this.fail(`Overview videos exceed the ${this.OVERVIEW_MAX_VIDEOS} video limit.`);
      }
      if (!this.details.startDate || !this.details.startTime) return this.fail('Start date and time is required.');
      if (!this.details.endDate || !this.details.endTime) return this.fail('End date and time is required.');
      if (this.details.locationMode === 'physical' && !this.selectedLocation) {
        return this.fail('Please search and select a physical location.');
      }
      return true;
    }

    if (step === 2) {
      if (!this.tickets.length) return this.fail('At least one ticket is required.');
      const invalidTicket = this.tickets.find((ticket) => !ticket.name.trim());
      if (invalidTicket) return this.fail('All tickets must have a name.');
      return true;
    }

    if (step === 3) {
      if (this.settings.maxTicketsPerOrder <= 0) return this.fail('Ticket limit per order must be greater than 0.');
      if (this.settings.checkoutTimeLimitMinutes <= 0) return this.fail('Checkout time limit must be greater than 0.');
      return true;
    }

    return true;
  }

  private fail(message: string): false {
    this.error = message;
    return false;
  }

  private slugify(value: string): string {
    return value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 70);
  }

  private toIso(date: string, time: string): string {
    return `${date}T${time}:00+07:00`;
  }

  async submit(): Promise<void> {
    this.error = '';
    this.success = '';
    if (!this.validateStep(1)) return;

    this.submitting = true;

    try {
      const overviewStorageJson = this.buildOverviewStoragePayload();
      const eventDetailsPayload = JSON.parse(overviewStorageJson);
      const eventPayload: Record<string, unknown> = {
        title: this.details.title.trim(),
        status: this.editEventId ? this.existingEventStatus : 'draft',
        event_type: 'ticketed',
        start_date: this.toIso(this.details.startDate, this.details.startTime),
        end_date: this.toIso(this.details.endDate, this.details.endTime),
        timezone: this.JAKARTA_TIMEZONE,
        location_mode: this.details.locationMode,
        location:
          this.details.locationMode === 'physical'
            ? this.selectedLocation?.venueName || ''
            : 'Online',
        location_address:
          this.details.locationMode === 'physical'
            ? this.selectedLocation?.address || null
            : null,
        online_meeting_url: this.details.onlineMeetingUrl.trim() || null,
        description: overviewStorageJson,
        event_details: eventDetailsPayload,
        image: this.coverImageFileId.trim() || null,
        eventcategory_id: this.details.categoryId ? Number(this.details.categoryId) : null,
        pic_id: this.details.projectDirectorId || null,
        event_location:
          this.details.locationMode === 'physical'
            ? {
                mode: 'physical',
                venue_name: this.selectedLocation?.venueName || '',
                address: this.selectedLocation?.address || '',
                map_query: this.selectedLocation?.mapQuery || ''
              }
            : {
                mode: 'online',
                url: this.details.onlineMeetingUrl.trim()
              }
      };

      let eventId = this.editEventId;
      if (this.editEventId) {
        await this.directus.update('events', this.editEventId, eventPayload);
      } else {
        const createdEvent = await this.directus.create('events', {
          ...eventPayload,
          slug: this.slugify(this.details.title) || `event-${Date.now()}`,
          publicEvent: true,
          paid: true
        });
        eventId = String(createdEvent['id'] ?? '');
        if (!eventId) throw new Error('Failed to create event (missing ID).');
      }
      await this.cleanupUnusedEventMediaFiles(String(eventId || this.editEventId || '').trim());
      this.success = this.editEventId
        ? 'Event details updated.'
        : 'Event details saved. Continue setup in Tickets & seating.';
      if (this.editEventId) {
        void this.router.navigateByUrl(`/events/${encodeURIComponent(eventId)}/${this.returnTab}`);
      } else {
        void this.router.navigateByUrl(`/events/${encodeURIComponent(eventId)}/tickets-seating`);
      }
    } catch (error: unknown) {
      this.error = error instanceof Error
        ? error.message
        : `Failed to ${this.editEventId ? 'update' : 'create'} event.`;
    } finally {
      this.submitting = false;
    }
  }

  private async loadEventForEdit(): Promise<void> {
    const rows = await this.safeList('events', {
      fields:
        'id,title,status,eventcategory_id,image,start_date,end_date,location_mode,location,location_address,online_meeting_url,description,event_details',
      'filter[id][_eq]': this.editEventId,
      limit: 1
    });
    const row = rows[0];
    if (!row) {
      this.error = 'Event not found.';
      return;
    }
    const start = this.parseDateTime(row['start_date']);
    const end = this.parseDateTime(row['end_date']);
    const locationModeRaw = typeof row['location_mode'] === 'string' ? row['location_mode'] : 'physical';
    const locationMode = locationModeRaw === 'online' ? 'online' : 'physical';
    const imageId = this.extractFileId(row['image']);
    const location = typeof row['location'] === 'string' ? row['location'] : '';
    const locationAddress = typeof row['location_address'] === 'string' ? row['location_address'] : '';

    this.details.title = typeof row['title'] === 'string' ? row['title'] : '';
    this.existingEventStatus =
      typeof row['status'] === 'string' && row['status'].trim()
        ? row['status'].trim()
        : 'draft';
    this.details.categoryId = row['eventcategory_id'] === null || row['eventcategory_id'] === undefined ? '' : String(row['eventcategory_id']);
    this.details.imageId = imageId;
    this.details.startDate = start.date;
    this.details.startTime = start.time;
    this.details.endDate = end.date;
    this.details.endTime = end.time;
    this.details.locationMode = locationMode;
    this.details.location = location;
    this.details.locationAddress = locationAddress;
    this.details.onlineMeetingUrl = typeof row['online_meeting_url'] === 'string' ? row['online_meeting_url'] : '';
    this.details.descriptionPublic = typeof row['description'] === 'string' ? row['description'] : '';
    this.details.projectDirectorId = '';
    this.details.projectDirectorPhone = '';

    this.syncCoverImageState(imageId);

    if (locationMode === 'physical' && (location || locationAddress)) {
      this.selectedLocation = {
        venueName: location || 'Venue',
        address: locationAddress || location,
        mapQuery: locationAddress || location
      };
      this.updateMapEmbedUrl();
    } else {
      this.selectedLocation = null;
      this.googleMapEmbedUrlSafe = this.sanitizer.bypassSecurityTrustResourceUrl('about:blank');
    }
    const loadedStructured = this.parseOverviewStoragePayload(this.extractOverviewStructuredRaw(row));
    if (!loadedStructured) {
      this.overviewModules = [this.createTextModule(this.details.descriptionPublic || '')];
      this.highlightDoorTimeValue = null;
      this.highlightDoorTimeUnit = 'minutes';
      this.highlightDressCode = '';
      this.highlightParking = '';
      this.faqItems = [];
      this.lineupSpeakers = [];
      this.eventSlides = imageId
        ? [
            {
              id: this.createLocalId(),
              type: 'image',
              fileId: imageId,
              assetUrl: this.buildAssetUrl(imageId),
              cropX: 0,
              cropY: 0,
              cropZoom: 1,
              cropBoxX: 0,
              cropBoxY: 25,
              cropBoxWidth: 100,
              cropBoxHeight: 50
            }
          ]
        : [];
      this.syncCoverImageState(imageId);
      void this.refreshImageSlidePreviews();
    }
    this.collapseSectionsForEdit();
    this.originalEventMediaFileIds.clear();
    for (const fileId of this.collectCurrentEventMediaFileIds(imageId)) {
      this.originalEventMediaFileIds.add(fileId);
    }
  }

  private collapseSectionsForEdit(): void {
    this.mediaEditorOpen = false;
    this.overviewCollapsed = true;
    this.dateLocationCollapsed = true;
    this.goodToKnowCollapsed = true;
    this.highlightsModalOpen = false;
  }

  private parseReturnTab(rawValue: string | null): string {
    const tab = String(rawValue || '').trim();
    return EventCreateWizardComponent.ALLOWED_RETURN_TABS.has(tab) ? tab : 'overview';
  }

  private parseDateTime(value: unknown): { date: string; time: string } {
    if (typeof value === 'string' && value.trim()) {
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) {
        const parts = new Intl.DateTimeFormat('en-CA', {
          timeZone: this.JAKARTA_TIMEZONE,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }).formatToParts(parsed);
        const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
        const date = `${values['year'] || ''}-${values['month'] || ''}-${values['day'] || ''}`;
        const time = `${values['hour'] || ''}:${values['minute'] || ''}`;
        if (date.length === 10 && time.length === 5) return { date, time };
      }
    }
    return { date: '', time: '' };
  }

  private collectCurrentEventMediaFileIds(explicitCoverImageId = ''): Set<string> {
    const ids = new Set<string>();
    const cover = String(explicitCoverImageId || this.details.imageId || '').trim();
    if (cover) ids.add(cover);
    this.overviewModules.forEach((module) => {
      const fileId = String(module.fileId || '').trim();
      if (fileId) ids.add(fileId);
    });
    this.eventSlides.forEach((slide) => {
      const fileId = String(slide.fileId || '').trim();
      if (fileId) ids.add(fileId);
    });
    this.lineupSpeakers.forEach((speaker) => {
      const fileId = String(speaker.fileId || '').trim();
      if (fileId) ids.add(fileId);
    });
    return ids;
  }

  private async cleanupUnusedEventMediaFiles(eventId: string): Promise<void> {
    const keepIds = this.collectCurrentEventMediaFileIds(this.coverImageFileId);
    const candidates = new Set<string>([
      ...Array.from(this.originalEventMediaFileIds),
      ...Array.from(this.uploadedSlideMediaFileIds)
    ]);
    keepIds.forEach((id) => candidates.delete(id));
    if (!candidates.size) return;

    const deletions = Array.from(candidates).map(async (fileId) => {
      const stillReferenced = await this.isMediaFileReferencedByEvents(fileId, eventId);
      if (stillReferenced) return;
      await this.directus.removeFile(fileId);
    });
    await Promise.allSettled(deletions);
  }

  private async isMediaFileReferencedByEvents(fileId: string, currentEventId: string): Promise<boolean> {
    const safeId = String(fileId || '').trim();
    if (!safeId) return false;
    try {
      const rows = await this.directus.list('events', {
        fields: 'id,image,description,event_details',
        ...(currentEventId ? { 'filter[id][_neq]': currentEventId } : {}),
        limit: 500
      });
      return rows.some((row) => this.eventRowContainsMediaReference(row, safeId));
    } catch {
      // Fail safe: if we can't verify references, skip deletion.
      return true;
    }
  }

  private eventRowContainsMediaReference(row: Record<string, unknown>, fileId: string): boolean {
    const imageId = this.extractFileId(row['image']);
    if (imageId === fileId) return true;

    const description = typeof row['description'] === 'string' ? row['description'] : '';
    if (description.includes(fileId)) return true;

    const eventDetails = row['event_details'];
    if (typeof eventDetails === 'string') return eventDetails.includes(fileId);
    if (eventDetails && typeof eventDetails === 'object') {
      try {
        return JSON.stringify(eventDetails).includes(fileId);
      } catch {
        return false;
      }
    }
    return false;
  }

  private extractFileId(value: unknown): string {
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object' && 'id' in value) {
      const id = (value as { id?: unknown }).id;
      if (typeof id === 'string') return id;
    }
    return '';
  }
}
